import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ViewEncapsulation,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  getDebugNode,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OverlayContainer } from '@angular/cdk/overlay';
import { PlaybackPageComponent, TrimSelection } from '@ecam/eventq-components/playback';
import type { PlaybackEvents } from '@ecam/eventq-components/playback';
import { BehaviorSubject, firstValueFrom, fromEvent, type Subscription } from 'rxjs';
import type {
  AiFeedbackVariant,
  CameraPtzAction,
  CameraReportReasonId,
  EventqDetectionBoxClick,
  EventqSelectOption,
  EventqVideoContentLayout,
} from '@ecam/eventq-components';
import { BrowserTabService } from '../../../../core/services/browser-tab.service';
import { CurrentTimeService } from '../../../../core/services/current-time.service';
import { parseClipLocations } from '../../../../core/api/dto-mappers';
import { isWhepPlaybackUrl } from '../../../../core/api/streaming/streaming.mapper';
import { StreamingService } from '../../../../core/services/streaming.service';
import { WhepPlayerService } from '../../../../core/services/whep-player.service';
import { resolveComponentFromHostElement } from './resolve-host-component';
import {
  CAMERA_REPORT_REASON_OPTIONS,
  EventqAiFeedbackPopover,
  EventqDetectionOverlay,
  EventqBadge,
  EventqDatetimePicker,
  EventqFlagReportPopover,
  EventqMiniPlaybackControls,
  EventqPtzControls,
  EventqSelect,
  EventqToastService,
  PrefixUrlPipe,
  computeVideoRenderMetrics,
} from '@ecam/eventq-components';
import type { PlaybackViewMode } from '../../../../core/models/browser-tab.models';
import type {
  EventAiTagVariant,
  EventAlarmDetection,
  EventAlarmRow,
  EventCameraItem,
} from '../../../../core/models/entities/event-detail.model';
import type { DetectionBoundingBox } from '../../../../core/models/entities/event-playback.model';
import {
  buildClipAssetRequest,
  buildSnapshotAssetRequest,
} from '../../../../core/api/resolve/resolve-event-asset.mapper';
import {
  buildDetectionBoxesFromAiSegments,
  filterBoxesByEventTypes,
  stampAlarmClipDetectionBoxes,
} from '../../utils/detection-bbox.util';
import {
  mapHistoricAlarmsToTimelineSegments,
  type HistoricPlaybackAlarmSegments,
} from '../../../../core/api/resolve/historic-alarm-timeline.mapper';
import {
  EventPlaybackService,
  PLAYBACK_MOTION_OPTIONS,
  type FetchLiveSegmentsOptions,
  type PlaybackCamera,
} from '../../services/event-playback.service';
import {
  fromNvrWallClockDate,
  nvrWallClockAsUtcIso,
  parseUtcInstant,
  playbackTimeZoneAbbreviation,
  resolvePlaybackTimeZone,
  toNvrWallClockDate,
} from '../../utils/nvr-timezone.util';
import {
  computePlaybackWindow,
  formatPlaybackDuration,
  parseEventAlarmTimestamp,
  previewStartOffsetSeconds,
  PLAYBACK_DEFAULT_RANGE_SECONDS,
  type PlaybackWindow,
} from '../../utils/playback-window.util';
import {
  ALARM_CLIP_SECONDS_PER_FRAME,
  alarmClipDurationSeconds,
  normalizeAlarmClipElapsedSeconds,
  resolveAlarmClipFrameIndex,
} from '../../utils/alarm-image-clip.util';
import {
  isImageMediaUrl,
  requiresAuthenticatedMediaFetch,
  resolveMediaAssetUrl,
  resolveSameOriginMediaFetchUrl,
} from '../../utils/media-url.util';
import {
  collectLibraryTimelineNavEvents,
  resolveLibraryTimelineNavTarget,
  type LibraryTimelineNavDirection,
  type LibraryTimelineNavScope,
} from '../../utils/library-timeline-nav.util';
import { MOCK_ARCHIVE_SEGMENT_KEY } from '../../utils/expand-mock-playback-segments';
import {
  resolveMediaSegmentAtTime,
  trySoftSeekPlayingVideo,
} from '../../utils/library-playing-seek.util';
import {
  captureFetchedImageAsPngBase64,
  captureImageElementAsPngBase64,
  captureVideoFrameAsPngBase64,
} from '../../utils/capture-video-media.util';

export type EventPlaybackHostMode = 'playback' | 'live';
export type EventPlaybackAiFeedbackScope = 'inline' | 'view-alarm';
/** `archive` plays recorded segments; `webrtc` plays the camera's initialized live stream. */
export type EventPlaybackVideoSource = 'archive' | 'webrtc';

const LIBRARY_PLAYBACK_SPEEDS = [1, 1.5, 2] as const;
const STREAM_CONNECT_TIMEOUT_MS = 8_000;
/** Panels the shared components portal to `<body>` when opened. */
const PORTALED_PANEL_SELECTOR = '.eventq-select__menu, .eventq-datetime-picker__panel';

/** Unexported Caliber `lib-player` instance shape used by timeline helpers. */
type LibraryPlayerInstance = {
  selectedMotions: string[];
  selectedSpeed?: number;
  speeds?: number[];
  startDate: Date;
  endDate: Date;
  currentTickerTime: Date;
  /** IANA zone used to label the timeline clock. */
  tickerTimeZone?: string;
  /** Calendar selection that rebuilds must not replace with the old scroll time. */
  pinnedPlayhead?: Date | null;
  activeEventType?: string | null;
  forceSeekNextUpdate?: boolean;
  min: number;
  max: number;
  step: number;
  sliderValue: number;
  pixelsPerMinute: number;
  rebuildTimeline: () => void;
  rebuildEventIndex?: () => void;
  onRangeChange?: (hours: number) => void;
  syncStagePlayback?: (forceReload?: boolean) => void;
  isPlaying?: boolean;
  engine?: {
    getTimeFromPixel: (pixel: number) => Date;
    getPixel?: (time: Date) => number;
    padding?: number;
  };
  timelineScroller?: { nativeElement: HTMLElement };
  /** X of the white cursor inside the scroll area, including the camera-name shift. */
  cursorOffsetInViewport?: () => number;
  selectedCameraList?: Array<{
    id: string | number;
    segments?: Record<
      string,
      Array<{ start: Date | string; end: Date | string; url?: string; cameraId?: string }>
    >;
  }>;
  selectedMotionOptions?: Array<{ value: string }>;
  trimMode?: boolean;
  activeTrimSelection?: unknown | null;
  onTrimClick?: () => void;
  onTrimClose?: () => void;
  startTrimSelection?: (
    camera: unknown,
    segment: { start: Date | string; end: Date | string },
    eventType: string,
  ) => void;
  onSpeedChange?: (speed: number) => void;
  onEndDateChange?: (value: string) => void;
  seekToTime?: (time: Date, syncFromScroll?: boolean) => void;
  onPause?: () => void;
  onPlay?: () => void;
  /** Timeline drag release (viewport mouseup / mouseleave). */
  onDragEnd?: () => void;
  isDragging?: boolean;
  skipForward?: (seconds?: number) => void;
  skipBackward?: (seconds?: number) => void;
  nextFrame?: () => void;
  previousFrame?: () => void;
  nextEvent?: () => void;
  previousEvent?: () => void;
  __eventqTrimWired?: boolean;
  __eventqVisibleFetchWired?: boolean;
  __eventqNavWired?: boolean;
  __eventqArchiveSeekWired?: boolean;
  __eventqPlayheadSeekWired?: boolean;
  __eventqRangeWired?: boolean;
  __eventqPauseWired?: boolean;
};

/** Unexported Caliber `lib-stage` bits needed for playing seeks. */
type LibraryStageInstance = {
  seek?: (
    cameraId: string | number,
    segment: { start: Date | string; end: Date | string; url?: string },
    timestamp: Date,
    isPlaying: boolean,
    playbackRate: number,
    forceReload?: boolean,
  ) => void;
  getVideoElement?: (cameraId: string | number) => HTMLVideoElement | null | undefined;
  playerMap?: Map<
    string | number,
    { currentUrl: string; segmentUrl: string; loadedStartTime: Date }
  >;
  videoPlayers?: {
    first?: { nativeElement: HTMLVideoElement };
  };
};

/** Same wall-clock instant. A fresh `Date` for that instant is not a new picker value. */
function samePickerInstant(previous: Date | null, next: Date | null): boolean {
  if (previous === next) {
    return true;
  }
  if (previous === null || next === null) {
    return false;
  }
  return previous.getTime() === next.getTime();
}

function isSnapshotImageLimitReached(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse) || error.status !== 409) {
    return false;
  }
  const body: unknown = error.error;
  if (!body || typeof body !== 'object') {
    return false;
  }
  return (body as { code?: unknown }).code === 'MAX_EVENT_IMAGE_ASSETS_EXCEEDED';
}

@Component({
  selector: 'app-event-playback-host',
  imports: [
    PlaybackPageComponent,
    EventqDetectionOverlay,
    EventqMiniPlaybackControls,
    EventqBadge,
    EventqDatetimePicker,
    EventqSelect,
    EventqFlagReportPopover,
    EventqAiFeedbackPopover,
    EventqPtzControls,
    PrefixUrlPipe,
  ],
  templateUrl: './event-playback-host.html',
  styleUrl: './event-playback-host.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class EventPlaybackHostComponent {
  private readonly playbackService = inject(EventPlaybackService);
  private readonly http = inject(HttpClient);
  private readonly streaming = inject(StreamingService);
  private readonly whepPlayer = inject(WhepPlayerService);
  /** Playback can only look backwards, so the tile picker stops at the current time. */
  readonly latestSelectableDate = inject(CurrentTimeService).now;
  private readonly browserTabs = inject(BrowserTabService);
  private readonly toast = inject(EventqToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly overlayContainer = inject(OverlayContainer);
  private playbackBootstrapSeq = 0;

  /** Transport skip step, shared by the button labels and the jump itself. */
  readonly skipSeconds = 15;

  readonly mode = input<EventPlaybackHostMode>('playback');
  readonly eventId = input.required<string>();
  /** Dealer site id — used to load historic alarms on playback library fullscreen. */
  readonly siteId = input<number | null>(null);
  /** Dealer site name — required for live camera report `POST /case`. */
  readonly siteName = input('');
  readonly cameraId = input<string | null>(null);
  readonly cameras = input<EventCameraItem[]>([]);
  readonly headerTitle = input('');
  readonly headerTimestamp = input<string | undefined>(undefined);
  readonly visibleDetectionTypes = input<EventAiTagVariant[]>([]);
  /**
   * When true, an empty `visibleDetectionTypes` hides overlay boxes and timeline AI bars
   * instead of treating empty as “show all” (Event View default).
   */
  readonly strictVisibleDetectionTypes = input(false);
  /** When false, detection boxes are not clickable and the AI feedback popover stays closed. */
  readonly aiFeedbackEnabled = input(true);
  readonly streamType = input<'main' | 'sub'>('main');
  /**
   * Playback tiles default to recorded segments. `webrtc` swaps the tile onto the
   * camera's RTSP stream via POST /legacy/initialize (Playback tab grid).
   * Fullscreen keeps the archive timeline regardless.
   */
  readonly videoSource = input<EventPlaybackVideoSource>('archive');
  /** When true (PTZ cameras), show a PTZ chip left of the HD toggle that toggles pad visibility. */
  readonly showPtzToggle = input(false);
  readonly playbackViewMode = input<PlaybackViewMode>('clip');
  /** When false, clip/playback toggle is hidden (Live Cameras / Playback tabs). */
  readonly allowClipToggle = input(true);
  readonly showPlaybackDatetimePicker = input(true);
  readonly showFullscreenAction = input(true);
  /**
   * How Enter fullscreen behaves:
   * - `library` — host element fullscreen + Caliber timeline (Playback tab)
   * - `popover` — emit `fullscreenRequest` so the parent opens a popout (View Alarm / Live)
   */
  readonly fullscreenMode = input<'library' | 'popover'>('library');
  /**
   * Set by a parent popout so this tile renders the fullscreen action as Exit;
   * toggling it emits `fullscreenRequest` again for the parent to close the popout.
   */
  readonly fullscreenActive = input(false);
  /** How preview video fills its stage — `contain` keeps the native aspect ratio (letterbox OK). */
  readonly videoObjectFit = input<'cover' | 'contain'>('contain');
  /** Whether playback-mode window is centered on, starts at, or ends at the anchor time. */
  readonly playbackAnchorMode = input<'center' | 'start' | 'end'>('center');
  readonly playbackCustomRangeSeconds = input<number | null>(null);
  readonly playbackAnchorTimestamp = input<string | null>(null);
  readonly selectedAlarmId = input<string | null>(null);
  /** Direct clip URL for the selected alarm (Alarms tab / View Alarm popover). */
  readonly alarmClipUrl = input<string | null>(null);
  /** Still-image frames for alarm clip mode (typically five JPEGs from Dealer). */
  readonly alarmClipUrls = input<readonly string[]>([]);
  readonly alarmDetections = input<readonly EventAlarmDetection[]>([]);
  readonly alarmTimestamp = input<string | null>(null);
  readonly snapshotEnabled = input(true);
  /** When false, Attach alarm clip control is hidden even in clip mode. */
  readonly attachAlarmClipEnabled = input(true);
  /** Which tab-state slice drives AI feedback for this host instance. */
  readonly aiFeedbackScope = input<EventPlaybackAiFeedbackScope>('inline');
  /**
   * When true, show Caliber stage + timeline inline (Showcase) without browser fullscreen.
   * Fullscreen playback also enables the library timeline.
   */
  readonly libraryTimeline = input(false);

  readonly streamTypeChange = output<'main' | 'sub'>();
  readonly playbackViewModeChange = output<PlaybackViewMode>();
  readonly playbackAnchorTimestampChange = output<string>();
  /** Emits when Alarms-tab fullscreen should open View Alarm at the current playback instant. */
  readonly fullscreenRequest = output<string | null>();
  /** Emits when the library-timeline AI Events select changes (Showcase / FS filters). */
  readonly visibleDetectionTypesChange = output<EventAiTagVariant[]>();
  /** Emits the alarm id after its clip/image preview loads successfully in playback mode. */
  readonly alarmClipViewed = output<string>();

  readonly motionOptions = PLAYBACK_MOTION_OPTIONS;
  readonly librarySpeedOptions: EventqSelectOption[] = LIBRARY_PLAYBACK_SPEEDS.map((speed) => ({
    id: String(speed),
    label: `${speed}X`,
  }));
  /** Figma 5989:46228 — colored AI event rows with leading icons. */
  readonly libraryAiEventOptions: EventqSelectOption[] = [
    {
      id: 'motion',
      label: 'Motion',
      iconSrc: '/assets/events/icons/ai-events/motion.svg',
      color: 'var(--eventq-color-ecam-orange, #f4af00)',
    },
    {
      id: 'human',
      label: 'Human',
      iconSrc: '/assets/events/icons/ai-events/human.svg',
      color: 'var(--eventq-color-blue-600, #1e88e5)',
    },
    {
      id: 'vehicle',
      label: 'Vehicle',
      iconSrc: '/assets/events/icons/ai-events/vehicle.svg',
      color: 'var(--eventq-color-green-600, #43a047)',
    },
    {
      id: 'gun',
      label: 'Gun',
      iconSrc: '/assets/events/icons/ai-events/gun.svg',
      color: 'var(--eventq-color-red-600, #e53935)',
    },
  ];
  readonly updateSelectedCameras$ = new BehaviorSubject<PlaybackCamera[]>([]);
  /** Kept false — EventQ owns browser fullscreen; Caliber must not call requestFullscreen. */
  readonly fullScreenLib$ = new BehaviorSubject<boolean>(false);
  readonly playbackPage = viewChild(PlaybackPageComponent);
  readonly previewVideoRef = viewChild<ElementRef<HTMLVideoElement>>('previewVideo');
  readonly previewImageRef = viewChild<ElementRef<HTMLImageElement>>('previewImage');
  readonly stageWrapRef = viewChild<ElementRef<HTMLElement>>('stageWrap');
  readonly hostRootRef = viewChild<ElementRef<HTMLElement>>('hostRoot');
  private readonly datetimePickers = viewChildren(EventqDatetimePicker);
  private readonly chromeSelects = viewChildren(EventqSelect);

  readonly isFullscreen = computed(
    () => this.fullscreenActive() || this.browserTabs.activePageState().playbackFullscreen,
  );
  /**
   * A live popout duplicates its camera's tile, and both copies read the same tab
   * state — only the copy on the visible surface may render camera panels.
   */
  private readonly ownsCameraPanels = computed(
    () =>
      (this.browserTabs.activePageState().liveCameraPopoutCameraId !== null) ===
      this.fullscreenActive(),
  );
  /** PTZ pad open only for the camera tile that owns tab state. */
  readonly ptzControlsOpen = computed(() => {
    const state = this.browserTabs.activePageState();
    const cameraId = this.cameraId();
    return (
      state.playbackPtzControlsOpen &&
      cameraId != null &&
      state.playbackPtzControlsCameraId === cameraId &&
      this.ownsCameraPanels()
    );
  });
  readonly flagReportOpen = computed(() => {
    const state = this.browserTabs.activePageState();
    const cameraId = this.cameraId();
    return (
      state.playbackFlagReportOpen &&
      cameraId != null &&
      state.playbackFlagReportCameraId === cameraId &&
      this.ownsCameraPanels()
    );
  });
  readonly selectedReportReasonId = computed(
    () => this.browserTabs.activePageState().playbackSelectedReportReasonId as CameraReportReasonId | null,
  );
  readonly submittingReport = signal(false);
  readonly submittingAiFeedback = signal(false);
  readonly submittingSnapshot = signal(false);
  readonly submittingAttachAlarmClip = signal(false);
  readonly aiFeedbackAnchor = signal<HTMLElement | null>(null);
  readonly selectedAiFeedbackBox = signal<DetectionBoundingBox | null>(null);
  readonly previewVideoUrl = signal<string | null>(null);
  /** True when preview media is a MediaMTX WHEP URL (no `<video src>`). */
  readonly previewUsesWhep = signal(false);
  /** WebRTC playback URL for `videoSource() === 'webrtc'` tiles (no `<video src>`). */
  readonly previewStreamUrl = signal<string | null>(null);
  readonly previewPlaying = signal(true);
  /** True from stream initialization until the first WHEP frames land. */
  readonly previewStreamConnecting = signal(false);
  /** True when a WHEP session did not produce frames before the connection timeout. */
  readonly previewStreamUnavailable = signal(false);
  /** Still of the last frame, shown while a paused tile has no live session. */
  readonly pausedFrameUrl = signal<string | null>(null);
  /** Forces WHEP to reconnect when initialize returns the same URL for a new time. */
  private readonly previewStreamSession = signal(0);
  readonly previewElapsedSeconds = signal(0);
  /** Native media duration when the preview `<video>` metadata is available. */
  readonly previewMediaDurationSeconds = signal(0);
  readonly detectionBoxes = signal<DetectionBoundingBox[]>([]);
  readonly stageSize = signal({ width: 0, height: 0 });
  readonly videoIntrinsicSize = signal({ width: 0, height: 0 });
  /** Video tile bounds inside stage-wrap (fullscreen timeline mode). */
  readonly videoStageBounds = signal({ top: 0, left: 0, width: 0, height: 0 });

  /** True when this host element (not merely tab chrome) owns document fullscreen. */
  readonly hostDocumentFullscreen = signal(false);

  /** Caliber stage + timeline (fullscreen Event View on Playback tab, or Showcase inline). */
  readonly useLibraryTimeline = computed(
    () =>
      this.mode() === 'playback' &&
      this.fullscreenMode() === 'library' &&
      (this.libraryTimeline() || this.hostDocumentFullscreen()),
  );

  /** Grid WHEP tiles start on sub; stay on WHEP if fullscreen later switches to main. */
  private readonly webrtcStreamArmed = signal(false);

  /** Playback-tab grid tiles and the Alarms-tab PLAYBACK toggle both negotiate WHEP. */
  private readonly usesWebrtcPlaybackPreview = computed(
    () =>
      this.mode() === 'playback' &&
      (!this.allowClipToggle() || this.effectivePlaybackViewMode() === 'playback'),
  );

  /** Sub tiles stream by default; main tiles only when `videoSource` opts in (View Alarm). */
  private readonly streamsWebrtcPlayback = computed(
    () => this.streamType() === 'sub' || this.videoSource() === 'webrtc',
  );

  readonly isWebrtcPlaybackTile = computed(
    () =>
      this.usesWebrtcPlaybackPreview() &&
      (this.streamsWebrtcPlayback() || this.webrtcStreamArmed()),
  );

  private readonly inAlarmsClipMode = computed(
    () =>
      this.mode() === 'playback' &&
      this.allowClipToggle() &&
      this.effectivePlaybackViewMode() === 'clip',
  );

  readonly alarmClipImageSources = computed(() => {
    const rawParts = this.alarmClipUrls().length
      ? this.alarmClipUrls()
      : this.alarmClipUrl()
        ? [this.alarmClipUrl() as string]
        : [];

    return rawParts
      .flatMap((part) => parseClipLocations(part))
      .filter((url) => isImageMediaUrl(url))
      .map((url) => resolveMediaAssetUrl(url))
      .filter(Boolean);
  });

  readonly alarmClipFrameUrls = signal<readonly string[]>([]);
  /** Original payload index for each playable still. Dropped failures leave a gap. */
  private readonly alarmClipSourceFrameIndexes = signal<readonly number[]>([]);
  /** Playable-list index of the still whose pixels have finished loading. */
  private readonly alarmClipShownFrameIndex = signal(0);
  readonly alarmClipImagesFailed = signal(false);

  readonly usesAlarmImageClip = computed(
    () => this.inAlarmsClipMode() && this.alarmClipImageSources().length > 0,
  );

  readonly activeAlarmClipFrameUrl = computed(() => {
    const urls = this.alarmClipFrameUrls();
    if (!this.usesAlarmImageClip() || !urls.length) {
      return null;
    }
    const index = resolveAlarmClipFrameIndex(this.previewElapsedSeconds(), urls.length);
    return urls[index] ?? null;
  });

  /**
   * Alarm clips stamp boxes with frame-index offsets; the overlay filters by
   * `Math.floor(activeOffsetSeconds)`, so pass the current frame index.
   */
  readonly detectionOverlayOffsetSeconds = computed(() => {
    if (this.usesAlarmImageClip()) {
      const shown = this.alarmClipShownFrameIndex();
      return this.alarmClipSourceFrameIndexes()[shown] ?? shown;
    }
    return this.previewElapsedSeconds();
  });

  /**
   * Alarm-clip stills swap immediately. A slide leaves the box behind the
   * vehicle for the whole move, so alarm clips never animate. Other playback
   * keeps the overlay slide.
   */
  readonly alarmClipBoxesAnimated = computed(() => !this.usesAlarmImageClip());

  readonly alarmClipImagesLoading = computed(
    () =>
      this.usesAlarmImageClip() &&
      this.alarmClipImageSources().length > 0 &&
      this.alarmClipFrameUrls().length === 0 &&
      !this.alarmClipImagesFailed(),
  );

  readonly showAlarmClipUnavailable = computed(
    () =>
      this.inAlarmsClipMode() &&
      (this.alarmClipImageSources().length === 0 || this.alarmClipImagesFailed()),
  );

  /** True when the tile has either a recorded clip or a live WebRTC stream to show. */
  readonly hasPreviewMedia = computed(
    () =>
      this.usesAlarmImageClip() ||
      !!this.previewVideoUrl() ||
      !!this.previewStreamUrl(),
  );

  /**
   * A WHEP tile with no media is sitting on an instant the NVR cannot serve, which
   * is exactly when the transport has to stay usable to move off it.
   */
  readonly transportDisabled = computed(
    () => !this.hasPreviewMedia() && !this.isWebrtcPlaybackTile(),
  );

  /** Live region label for library timeline connectivity chrome. */
  readonly streamStatusLabel = computed(() => {
    if (this.usesAlarmImageClip()) {
      if (this.alarmClipImagesFailed()) {
        return 'Unavailable';
      }
      if (this.alarmClipImagesLoading()) {
        return 'Loading';
      }
      return 'Online';
    }
    if (this.previewStreamUnavailable()) {
      return 'No recording';
    }
    return this.previewStreamConnecting() ? 'Connecting' : 'Online';
  });

  readonly detectionVideoLayout = computed((): EventqVideoContentLayout | null => {
    const { width: containerWidth, height: containerHeight } = this.stageSize();
    if (containerWidth <= 0 || containerHeight <= 0) {
      return null;
    }

    const { width: videoWidth, height: videoHeight } = this.alarmClipLayoutSize();
    if (videoWidth <= 0 || videoHeight <= 0) {
      // Alarm stills letterbox via object-fit; pretending the container is the
      // frame maps boxes into the black bars, then jumps when the image loads.
      if (this.usesAlarmImageClip()) {
        return null;
      }
      return {
        containerWidth,
        containerHeight,
        videoWidth: containerWidth,
        videoHeight: containerHeight,
        objectFit: this.videoObjectFit(),
      };
    }

    return {
      containerWidth,
      containerHeight,
      videoWidth,
      videoHeight,
      objectFit: this.videoObjectFit(),
    };
  });

  /**
   * Prefer measured media size; for alarm stills fall back to detection payload
   * dimensions so letterboxing is correct before `<img>` load.
   */
  readonly alarmClipLayoutSize = computed(() => {
    const intrinsic = this.videoIntrinsicSize();
    if (intrinsic.width > 0 && intrinsic.height > 0) {
      return intrinsic;
    }
    if (!this.usesAlarmImageClip()) {
      return intrinsic;
    }
    const sample = this.alarmDetections().find(
      (detection) => detection.sourceWidth > 0 && detection.sourceHeight > 0,
    );
    if (!sample) {
      return intrinsic;
    }
    return { width: sample.sourceWidth, height: sample.sourceHeight };
  });

  /** Alarm clips hide overlays until layout size is known (seeded or image-loaded). */
  readonly overlayDetectionBoxes = computed(() => {
    if (this.usesAlarmImageClip()) {
      if (this.alarmClipFrameUrls().length === 0) {
        return [];
      }
      const { width, height } = this.alarmClipLayoutSize();
      if (width <= 0 || height <= 0) {
        return [];
      }
    }
    return this.detectionBoxes();
  });

  readonly effectivePlaybackViewMode = computed<PlaybackViewMode>(() =>
    this.allowClipToggle() ? this.playbackViewMode() : 'playback',
  );

  /** Alarms tab / View Alarm — live tile and clip/playback host. Hidden on grid tabs. */
  readonly showAttachAlarmClip = computed(() => this.attachAlarmClipEnabled());

  /** Shows the mode the user can switch to (not the active mode). */
  readonly viewModeLabel = computed(
    () =>
      (this.effectivePlaybackViewMode() === 'clip' ? 'PLAYBACK' : 'CLIP') as 'CLIP' | 'PLAYBACK',
  );

  readonly playbackWindow = computed(() => {
    const anchor = this.resolveAnchorDate();
    if (!anchor) {
      return null;
    }

    return computePlaybackWindow(
      anchor,
      this.effectivePlaybackViewMode(),
      this.playbackCustomRangeSeconds(),
      this.playbackAnchorMode(),
    );
  });

  /** Prefer the configured playback window; fall back to media length for tiles. */
  readonly previewTimelineDurationSeconds = computed(() => {
    if (this.usesAlarmImageClip()) {
      return this.alarmClipCycleSeconds();
    }

    const windowDuration = this.playbackWindow()?.durationSeconds ?? 0;
    const base = windowDuration > 0 ? windowDuration : PLAYBACK_DEFAULT_RANGE_SECONDS;
    if (!this.isWebrtcPlaybackTile()) {
      return windowDuration > 0 ? windowDuration : this.previewMediaDurationSeconds();
    }

    // The motion window is fixed. Landing on its end must not open another
    // 15 seconds — that used to repeat for as long as the stream kept playing.
    const elapsed = Math.max(0, this.previewElapsedSeconds());
    if (elapsed <= base) {
      return base;
    }
    return base * (Math.floor(elapsed / base) + 1);
  });

  readonly webrtcStreamStartDate = computed(() => {
    if (!this.isWebrtcPlaybackTile()) {
      return null;
    }
    return new Date(this.resolveWebrtcStartMs() ?? Date.now());
  });

  readonly playbackCameraTimeZone = computed(
    () => this.playbackService.resolveCameraById(this.cameras(), this.cameraId())?.timeZone,
  );

  readonly webrtcStreamStartDateForPicker = computed(
    () => toNvrWallClockDate(this.webrtcStreamStartDate(), this.playbackCameraTimeZone()),
    { equal: samePickerInstant },
  );

  readonly playbackAnchorDateForPicker = computed(
    () => toNvrWallClockDate(this.playbackAnchorDate(), this.playbackCameraTimeZone()),
    { equal: samePickerInstant },
  );

  readonly libraryCenterDateForPicker = computed(
    () => toNvrWallClockDate(this.libraryCenterDate(), this.playbackCameraTimeZone()),
    { equal: samePickerInstant },
  );

  readonly pickerMaxDate = computed(() =>
    toNvrWallClockDate(this.latestSelectableDate(), this.playbackCameraTimeZone()),
  );

  readonly webrtcPickerTimeZoneAbbreviation = computed(() =>
    playbackTimeZoneAbbreviation(this.playbackCameraTimeZone(), this.webrtcStreamStartDate()),
  );

  readonly playbackAnchorTimeZoneAbbreviation = computed(() =>
    playbackTimeZoneAbbreviation(this.playbackCameraTimeZone(), this.playbackAnchorDate()),
  );

  readonly libraryPickerTimeZoneAbbreviation = computed(() =>
    playbackTimeZoneAbbreviation(this.playbackCameraTimeZone(), this.libraryCenterDate()),
  );

  readonly playbackDurationLabel = computed(() =>
    formatPlaybackDuration(Math.floor(this.previewTimelineDurationSeconds())),
  );

  readonly previewElapsedLabel = computed(() => {
    if (this.previewStreamUnavailable()) {
      return formatPlaybackDuration(0);
    }
    return formatPlaybackDuration(Math.floor(this.previewElapsedSeconds()));
  });

  readonly previewProgress = computed(() => {
    if (this.previewStreamUnavailable()) {
      return 0;
    }
    const duration = this.previewTimelineDurationSeconds();
    if (duration <= 0) {
      return 0;
    }
    return Math.min(100, (this.previewElapsedSeconds() / duration) * 100);
  });

  readonly playbackAnchorDate = computed(() => this.resolveAnchorDate());

  /** Playhead shown in the library chrome datetime (window center until the user scrubs). */
  readonly libraryCenterDate = signal<Date | null>(null);
  /** Frozen center of the ±slider-hours world; not moved by playhead drags. */
  private readonly libraryWindowCenter = signal<Date | null>(null);
  readonly librarySpeed = signal('1');
  /** Label on the chrome speed button, which steps through the options instead of opening a menu. */
  readonly librarySpeedLabel = computed(
    () =>
      this.librarySpeedOptions.find((option) => option.id === this.librarySpeed())?.label ?? '1X',
  );
  readonly libraryAiEventValues = signal<string[]>(
    PLAYBACK_MOTION_OPTIONS.map((option) => option.value),
  );

  readonly pageState = this.browserTabs.activePageState;

  readonly aiFeedbackOpen = computed(() => {
    const state = this.pageState();
    return this.aiFeedbackScope() === 'view-alarm'
      ? state.viewAlarmAiFeedbackOpen
      : state.aiFeedbackOpen;
  });

  readonly aiFeedbackDetectionId = computed(() => {
    const state = this.pageState();
    const id =
      this.aiFeedbackScope() === 'view-alarm'
        ? state.viewAlarmAiFeedbackDetectionId
        : state.aiFeedbackDetectionId;
    return id ?? '';
  });

  readonly aiFeedbackVariant = computed(() => {
    const state = this.pageState();
    const variant =
      this.aiFeedbackScope() === 'view-alarm'
        ? state.viewAlarmAiFeedbackVariant
        : state.aiFeedbackVariant;
    return (variant ?? 'motion') as AiFeedbackVariant;
  });

  /**
   * Inline AI feedback lives in tab page state so only one popover can own it across
   * multi-camera grid hosts. View-alarm scope is always a single host.
   */
  readonly showAiFeedbackPopover = computed(() => {
    if (!this.aiFeedbackOpen() || !this.aiFeedbackDetectionId()) {
      return false;
    }
    if (this.aiFeedbackScope() === 'view-alarm') {
      return true;
    }
    const ownerCameraId = this.pageState().aiFeedbackCameraId;
    const cameraId = this.cameraId();
    return (
      ownerCameraId != null &&
      cameraId != null &&
      ownerCameraId === cameraId &&
      this.ownsCameraPanels()
    );
  });

  cameraList: PlaybackCamera[] = [];
  private historicAlarmSegments: HistoricPlaybackAlarmSegments | null = null;
  private historicAlarmRows: EventAlarmRow[] | null = null;
  private historicAlarmCameraId: number | null = null;
  private historicAlarmsLoadKey: string | null = null;
  private stageTimeSub: Subscription | null = null;
  private overlayParentBeforeFullscreen: Node | null = null;
  /** True while this host instance owns document fullscreen (not shared tab chrome). */
  private localFullscreenOwned = false;
  private libraryVisibleFetchTimer: ReturnType<typeof setTimeout> | null = null;
  private libraryLoadedWindow: { start: number; end: number } | null = null;
  private libraryViewportUnbind: (() => void) | null = null;
  private suppressLibraryStreamReinitialize = false;
  /** A paused Apply seeks first, then pauses again after the new stream loads. */
  private pauseLibraryAfterSeek = false;
  /** Set while a stream restart is in flight so the timeline keeps the user's view. */
  private libraryPreserveViewOnConfigure = false;
  /** Watches `<body>` for chrome panels to pull into the fullscreen subtree. */
  private portaledPanelObserver: MutationObserver | null = null;
  /** Last successfully initialized WHEP URL still owned by this host. */
  private lastWebrtcUrl: string | null = null;
  /** In-flight teardown of this host's previous initialize path, if any. */
  private pendingWebrtcDisconnect: Promise<void> | null = null;
  /** Closes the WHEP peer connection currently reading `lastWebrtcUrl`. */
  private releaseActiveWhepReader: (() => void) | null = null;
  /** Elapsed seconds a transport skip carries across its session restart. */
  private pendingWebrtcElapsedSeconds: number | null = null;
  /** Bootstrap sequence of a deliberate start-time restart still awaiting initialize. */
  private pendingWebrtcRestartSeq: number | null = null;
  /** Instant the paused tile stopped on, reopened as a new session on play. */
  private webrtcPausedAtMs: number | null = null;
  /**
   * Playback-tab Pause closed the WHEP session. Playhead moves stay local until Play.
   * Distinct from Caliber `isPlaying`, which is false while a stream is still autoplaying.
   */
  private libraryPlaybackPaused = false;
  /**
   * Displayed elapsed at the moment the running WHEP session opened. The transport
   * counter keeps climbing across restarts, so only the seconds above this baseline
   * have actually played from the current session start.
   */
  private webrtcSessionElapsedBaseSeconds = 0;
  /**
   * Anchor the tile was on when a skip ran it up to the live edge. While it matches
   * the current anchor, the tile stays live instead of falling back to that anchor.
   */
  private webrtcLiveEdgeForAnchorMs: number | null = null;
  /** Camera + stream type + RTSP path + mode + view + start ms + library rate; a change disconnects the previous initialize path. */
  private activeStreamSessionKey = '';
  private alarmImageClipRafId: number | null = null;
  private alarmImageClipLastTickMs: number | null = null;
  private webrtcElapsedRafId: number | null = null;
  private webrtcElapsedLastTickMs: number | null = null;
  private libraryPlayheadRafId: number | null = null;
  private alarmClipLoadSeq = 0;
  private alarmClipObjectUrls: string[] = [];
  /** Alarm id plus image urls. A matching key is the same clip across a list refresh. */
  private alarmImageClipSessionKey = '';
  /** Video from this tile that entered Picture-in-Picture, even if the browser moved it. */
  private launchedPipVideo: HTMLVideoElement | null = null;
  /** Last event id applied to this tile. A change closes PiP before the next case attaches. */
  private pictureInPictureEventId: string | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.exitOwnedPictureInPicture();
      this.disconnectActiveWebrtcStream();
      this.stopAlarmImageClipTicker();
      this.stopWebrtcElapsedTicker();
      this.clearAlarmClipFrameUrls();
      this.unbindLibraryViewportLoading();
      this.unbindStageTimeUpdates();
      this.releasePortaledPanels();
      this.restoreOverlaysToBody();
    });

    fromEvent<Event>(this.hostElement.nativeElement, 'enterpictureinpicture')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.target instanceof HTMLVideoElement) {
          this.launchedPipVideo = event.target;
        }
      });

    effect(() => {
      const eventId = this.eventId();
      const previousEventId = untracked(() => this.pictureInPictureEventId);
      this.pictureInPictureEventId = eventId;
      if (previousEventId === null || previousEventId === eventId) {
        return;
      }
      if (!this.ownsPictureInPicture()) {
        return;
      }
      this.exitOwnedPictureInPicture();
      this.disconnectActiveWebrtcStream();
      this.activeStreamSessionKey = '';
    });

    fromEvent(document, 'fullscreenchange')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.hostDocumentFullscreen.set(this.thisHostOwnsDocumentFullscreen());
        void this.syncFullscreenState();
      });

    effect(() => {
      const sources = this.alarmClipImageSources();
      if (!this.usesAlarmImageClip()) {
        this.clearAlarmClipFrameUrls();
        return;
      }
      void this.hydrateAlarmClipFrameUrls(sources);
    });

    effect((onCleanup) => {
      const shouldTick =
        this.isWebrtcPlaybackTile() &&
        this.previewPlaying() &&
        !this.previewStreamConnecting() &&
        !this.previewStreamUnavailable() &&
        !this.useLibraryTimeline() &&
        !this.usesAlarmImageClip();
      if (!shouldTick) {
        return;
      }
      this.startWebrtcElapsedTicker();
      onCleanup(() => this.stopWebrtcElapsedTicker());
    });

    effect((onCleanup) => {
      const shouldTick =
        this.useLibraryTimeline() &&
        this.previewPlaying() &&
        !this.previewStreamConnecting() &&
        !this.previewStreamUnavailable();
      if (!shouldTick) {
        return;
      }
      this.startLibraryPlayheadTicker();
      onCleanup(() => this.stopLibraryPlayheadTicker());
    });

    effect(() => {
      const items = this.cameras();
      const selectedId = this.cameraId();
      this.playbackViewMode();
      this.allowClipToggle();
      this.playbackCustomRangeSeconds();
      this.headerTimestamp();
      this.playbackAnchorTimestamp();
      this.alarmClipUrls();
      this.alarmClipUrl();
      this.alarmClipImageSources();
      this.selectedAlarmId();
      this.streamType();

      this.cameraList = this.playbackService.buildCameraList(items, selectedId);
      this.applyHistoricAlarmSegmentsToCameraList();
      this.updateSelectedCameras$.next(this.cameraList);

      const selected = this.playbackService.resolveCameraById(items, selectedId);
      const wantsWebrtcPlayback =
        !!selected && this.usesWebrtcPlaybackPreview() && this.streamsWebrtcPlayback();
      if (wantsWebrtcPlayback) {
        this.webrtcStreamArmed.set(true);
      } else if (!selected || this.allowClipToggle()) {
        this.webrtcStreamArmed.set(false);
      }
      const sessionKey = selected ? this.resolveStreamSessionKey(selected.id) : '';
      const keepsConnectedSession =
        (wantsWebrtcPlayback || this.mode() === 'live') &&
        sessionKey === this.activeStreamSessionKey &&
        this.previewUsesWhep();
      if (keepsConnectedSession) {
        this.cameraList = this.playbackService.buildCameraList(items, selectedId);
        this.applyHistoricAlarmSegmentsToCameraList();
        // Same live session — drop any in-flight init so it cannot reset the playhead.
        // A restart owns its own start time, so cancelling it would strand the tile.
        if (this.pendingWebrtcRestartSeq === null) {
          this.playbackBootstrapSeq += 1;
        }
        return;
      }
      if (this.activeStreamSessionKey !== sessionKey) {
        this.libraryPlaybackPaused = false;
        this.disconnectActiveWebrtcStream();
        this.activeStreamSessionKey = sessionKey;
      }
      if (!selected) {
        this.detectionBoxes.set([]);
        this.previewVideoUrl.set(null);
        this.previewUsesWhep.set(false);
        this.previewStreamUrl.set(null);
        this.previewStreamConnecting.set(false);
        this.videoIntrinsicSize.set({ width: 0, height: 0 });
        return;
      }

      const libraryTimeline = this.useLibraryTimeline();

      if (this.mode() !== 'playback') {
        this.detectionBoxes.set([]);
      }
      if (this.mode() === 'live' || this.shouldUseWebrtcPreview()) {
        // Initialize + WHEP negotiation runs before any frame exists.
        this.previewStreamConnecting.set(true);
      }
      const bootstrapSeq = ++this.playbackBootstrapSeq;
      void this.bootstrapPlayback(selected, bootstrapSeq).then((completed) => {
        if (!completed || bootstrapSeq !== this.playbackBootstrapSeq) {
          return;
        }
        if (!libraryTimeline) {
          return;
        }
        // Bootstrap already aligned segments + configured the player; keep stage clocks live.
        this.bindStageTimeUpdates();
        queueMicrotask(() => this.configureLibraryTimeline());
      });
    });

    // Type toggles must only refresh overlays / motion filters — never rebootstrap the stream.
    effect(() => {
      const filterTypes = this.visibleDetectionTypes();
      this.strictVisibleDetectionTypes();
      const items = this.cameras();
      const selectedId = this.cameraId();
      const selected = this.playbackService.resolveCameraById(items, selectedId);
      if (!selected || this.mode() !== 'playback') {
        return;
      }

      // Alarm clips draw only the selected alarm's real detections.
      if (this.usesAlarmImageClip()) {
        this.applyAlarmClipDetectionBoxes();
        return;
      }

      // Live WHEP grid tiles have no recorded detections to overlay.
      if (this.shouldUseWebrtcPreview()) {
        this.detectionBoxes.set([]);
        return;
      }

      // Keep the library AI Events select aligned with parent-provided filters.
      if (filterTypes.length || this.strictVisibleDetectionTypes()) {
        this.libraryAiEventValues.set([...filterTypes]);
      }

      if (this.strictVisibleDetectionTypes() && !filterTypes.length) {
        this.detectionBoxes.set([]);
      } else {
        void this.loadDetections(selected, filterTypes, this.playbackWindow());
      }
      this.applyLibraryMotionFilter();
    });

    effect(() => {
      if (!this.aiFeedbackEnabled() && this.showAiFeedbackPopover()) {
        this.onAiFeedbackClose();
      }
    });

    afterRenderEffect((onCleanup) => {
      const webrtcUrl = this.previewStreamUrl();
      this.previewStreamSession();
      const video = this.previewVideoRef()?.nativeElement;
      if (!this.previewUsesWhep() || !webrtcUrl || !video) {
        return;
      }

      let disconnect: (() => void) | null = null;
      let superseded = false;
      void this.whepPlayer
        .attach(video, webrtcUrl)
        .then((cleanup) => {
          if (superseded) {
            cleanup();
            return;
          }
          disconnect = cleanup;
          this.releaseActiveWhepReader = cleanup;
          // A restart hands back a fresh element state — re-apply the chrome speed.
          untracked(() => this.applyLibraryPlaybackRate());
        })
        .catch(() => undefined);

      onCleanup(() => {
        superseded = true;
        if (this.releaseActiveWhepReader === disconnect) {
          this.releaseActiveWhepReader = null;
        }
        disconnect?.();
      });
    });

    effect((onCleanup) => {
      if (!this.previewStreamConnecting()) {
        return;
      }
      this.previewStreamUnavailable.set(false);
      const timeout = setTimeout(() => {
        this.previewStreamConnecting.set(false);
        this.previewStreamUnavailable.set(true);
        this.previewPlaying.set(false);
        this.previewElapsedSeconds.set(0);
        this.webrtcSessionElapsedBaseSeconds = 0;
        this.stopWebrtcElapsedTicker();
      }, STREAM_CONNECT_TIMEOUT_MS);
      onCleanup(() => clearTimeout(timeout));
    });

    effect(() => {
      this.latestSelectableDate();
      if (!untracked(() => this.useLibraryTimeline())) {
        return;
      }
      this.extendLibraryWindowToNow();
    });

    effect((onCleanup) => {
      const stage = this.stageWrapRef()?.nativeElement;
      if (!stage) {
        return;
      }

      // Re-measure when switching between preview and library timeline layouts.
      this.useLibraryTimeline();

      const updateStageSize = (): void => {
        if (this.useLibraryTimeline()) {
          this.syncLibraryChromePosition();
          const videoTile =
            (stage.querySelector('.body-container') as HTMLElement | null) ??
            (stage.querySelector('.video-container') as HTMLElement | null) ??
            (stage.querySelector('.videoPlayer') as HTMLElement | null);
          if (videoTile) {
            const stageRect = stage.getBoundingClientRect();
            const tileRect = videoTile.getBoundingClientRect();
            const bounds = {
              top: Math.max(0, tileRect.top - stageRect.top),
              left: Math.max(0, tileRect.left - stageRect.left),
              width: tileRect.width,
              height: tileRect.height,
            };
            this.videoStageBounds.set(bounds);
            this.stageSize.set({ width: bounds.width, height: bounds.height });

            const libVideo = stage.querySelector('.videoPlayer') as HTMLVideoElement | null;
            if (libVideo && libVideo.videoWidth > 0 && libVideo.videoHeight > 0) {
              this.videoIntrinsicSize.set({
                width: libVideo.videoWidth,
                height: libVideo.videoHeight,
              });
            }
            return;
          }
        }

        const rect = stage.getBoundingClientRect();
        this.videoStageBounds.set({ top: 0, left: 0, width: rect.width, height: rect.height });
        this.stageSize.set({ width: rect.width, height: rect.height });
      };

      updateStageSize();

      if (typeof ResizeObserver === 'undefined') {
        return;
      }

      const observer = new ResizeObserver(() => updateStageSize());
      observer.observe(stage);
      onCleanup(() => observer.disconnect());
    });
  }

  onRequestSegments(event: PlaybackEvents['requestSegments']): void {
    if (this.shouldUseWebrtcPreview()) {
      if (this.useLibraryTimeline()) {
        const player = this.resolveLibraryPlayer();
        const range = this.resolveNativeLibraryTimelineRange();
        if (player) {
          this.applyLibraryWindowRange(player, range);
        } else {
          this.seedLibraryWebrtcArchive(range);
        }
      }
      return;
    }

    if (this.mode() === 'live') {
      const numericId = event.cameraIds[0];
      const camera = this.cameras().find((item) => item.numericId === numericId);
      if (camera) {
        void this.playbackService.fetchLiveSegmentsAndUpdate(
          camera,
          this.cameraList,
          this.updateSelectedCameras$,
          this.streamType(),
          this.resolveWebrtcPlaybackOptions(),
        );
      }
    }
  }

  onDownload(event: TrimSelection): void {
    void this.playbackService.downloadClip(this.eventId(), event).catch(() => {
      this.toast.show('download-failed', {
        title: 'Clip download failed',
        subtitle: 'Could not export the selected playback clip. Try again.',
      });
    });
  }

  async onToggleFullscreen(): Promise<void> {
    if (this.fullscreenMode() === 'popover') {
      // Open and close both travel on this output; the parent owns the popout state.
      this.fullscreenRequest.emit(this.currentPlaybackInstant());
      return;
    }

    if (this.isFullscreen()) {
      if (document.fullscreenElement) {
        await document.exitFullscreen().catch(() => undefined);
      }
      return;
    }

    const root = this.hostRootRef()?.nativeElement;
    if (!root?.requestFullscreen) {
      return;
    }

    await root.requestFullscreen().catch(() => undefined);
  }

  private currentPlaybackInstant(): string | null {
    if (
      this.mode() !== 'playback' ||
      this.effectivePlaybackViewMode() !== 'playback' ||
      !this.isWebrtcPlaybackTile()
    ) {
      return null;
    }

    const streamStart = this.webrtcStreamStartDate();
    if (!streamStart || Number.isNaN(streamStart.getTime())) {
      return null;
    }

    const playedThisSession = Math.max(
      0,
      this.previewElapsedSeconds() - this.webrtcSessionElapsedBaseSeconds,
    );
    return new Date(streamStart.getTime() + playedThisSession * 1_000).toISOString();
  }

  onToggleStreamType(): void {
    this.streamTypeChange.emit(this.streamType() === 'main' ? 'sub' : 'main');
  }

  onTogglePtzControls(): void {
    if (this.ptzControlsOpen()) {
      this.browserTabs.updateActivePageState({
        playbackPtzControlsOpen: false,
        playbackPtzControlsCameraId: null,
      });
      return;
    }

    this.browserTabs.updateActivePageState({
      playbackPtzControlsOpen: true,
      playbackPtzControlsCameraId: this.cameraId(),
    });
  }

  onPtzCommand(action: CameraPtzAction): void {
    const camera = this.cameras().find((item) => item.id === this.cameraId());
    if (!camera) {
      return;
    }
    if (action === 'home' || action === 'presets' || action === 'stop') {
      return;
    }

    const needsFrame = action === 'left' || action === 'right' || action === 'up' || action === 'down';
    const frame = needsFrame ? this.livePtzFrame() : undefined;
    if (needsFrame && !frame) {
      return;
    }

    void firstValueFrom(
      this.playbackService.sendCameraPtz(camera.numericId, { action, frame }),
    ).catch(() => {
      this.toast.show('notice', {
        title: 'Camera move',
        subtitle: `Unable to move ${camera.name}.`,
        tone: 'error',
      });
    });
  }

  /**
   * Orchid 4-param centerPTZ uses the picture the operator sees.
   * The video element is `inset: 0`, so clientHeight can be thousands of
   * pixels — send the object-fit contain box, never WHEP 1920×1080.
   */
  private livePtzFrame(): { width: number; height: number } | undefined {
    const video = this.resolveLivePlayerElement();
    if (!video) {
      return undefined;
    }
    const box = video.getBoundingClientRect();
    const containerWidth = Math.floor(video.clientWidth || box.width);
    const containerHeight = Math.floor(video.clientHeight || box.height);
    const intrinsic = this.streamIntrinsicSize(video);
    if (!intrinsic || containerWidth <= 0 || containerHeight <= 0) {
      return undefined;
    }
    const painted = computeVideoRenderMetrics({
      containerWidth,
      containerHeight,
      videoWidth: intrinsic.width,
      videoHeight: intrinsic.height,
      objectFit: this.videoObjectFit(),
    });
    const width = Math.floor(painted.renderedWidth);
    const height = Math.floor(painted.renderedHeight);
    if (width <= 0 || height <= 0) {
      return undefined;
    }
    return { width, height };
  }

  private streamIntrinsicSize(video: HTMLVideoElement): { width: number; height: number } | undefined {
    const fromElement = { width: Math.floor(video.videoWidth), height: Math.floor(video.videoHeight) };
    if (fromElement.width > 0 && fromElement.height > 0) {
      return fromElement;
    }
    const srcObject = video.srcObject;
    const settings =
      srcObject && 'getVideoTracks' in srcObject
        ? srcObject.getVideoTracks()[0]?.getSettings()
        : undefined;
    const fromTrack = {
      width: Math.floor(settings?.width ?? 0),
      height: Math.floor(settings?.height ?? 0),
    };
    if (fromTrack.width > 0 && fromTrack.height > 0) {
      return fromTrack;
    }
    const cached = this.videoIntrinsicSize();
    if (cached.width > 0 && cached.height > 0) {
      return cached;
    }
    return undefined;
  }

  private resolveLivePlayerElement(): HTMLVideoElement | null {
    const preview = this.previewVideoRef()?.nativeElement;
    if (preview) {
      return preview;
    }

    const staged = this.stageWrapRef()?.nativeElement.querySelector('video');
    if (staged) {
      return staged;
    }

    const stage = this.playbackPage()?.stage as LibraryStageInstance | undefined;
    const camera = this.playbackService.resolveCameraById(this.cameras(), this.cameraId());
    return (
      (camera && typeof stage?.getVideoElement === 'function'
        ? (stage.getVideoElement(camera.id) ?? stage.getVideoElement(camera.numericId))
        : null) ??
      stage?.videoPlayers?.first?.nativeElement ??
      this.hostRootRef()?.nativeElement.querySelector('video') ??
      null
    );
  }

  onTogglePlaybackViewMode(): void {
    this.playbackViewModeChange.emit(this.playbackViewMode() === 'clip' ? 'playback' : 'clip');
  }

  onPlaybackAnchorApply(date: Date): void {
    const instant = fromNvrWallClockDate(date, this.playbackCameraTimeZone());
    this.playbackAnchorTimestampChange.emit(instant.toISOString());
  }

  onWebrtcStartApply(date: Date): void {
    if (!this.isWebrtcPlaybackTile()) {
      return;
    }
    const startMs = fromNvrWallClockDate(date, this.playbackCameraTimeZone()).getTime();
    if (!Number.isFinite(startMs) || startMs > Date.now()) {
      return;
    }
    this.persistWebrtcStartMs(Math.trunc(startMs));
    this.rebootstrapSelectedCamera();
  }

  /**
   * WHEP has no seek, so the transport moves by opening a session at the target
   * instant. Playback only looks backwards — a forward skip stops at the live edge.
   */
  onWebrtcSkip(deltaSeconds: number): void {
    if (!this.isWebrtcPlaybackTile()) {
      return;
    }

    const streamStart = this.webrtcStreamStartDate();
    if (!streamStart || Number.isNaN(streamStart.getTime())) {
      return;
    }

    const elapsed = Math.max(0, this.previewElapsedSeconds());
    // Only the seconds played since this session opened sit ahead of its start time.
    const playedThisSession = Math.max(0, elapsed - this.webrtcSessionElapsedBaseSeconds);
    const playheadMs = streamStart.getTime() + playedThisSession * 1_000;
    const targetMs = Math.min(
      Math.max(0, playheadMs + deltaSeconds * 1_000),
      this.latestSelectableDate().getTime(),
    );
    const movedSeconds = (targetMs - playheadMs) / 1_000;
    if (movedSeconds === 0) {
      return;
    }

    // The restart replays from its own start; keep the transport counting up.
    this.pendingWebrtcElapsedSeconds = Math.max(0, elapsed + movedSeconds);
    this.reinitializeWebrtcAt(new Date(targetMs));
  }

  onMiniSkip(deltaSeconds: number): void {
    if (this.isWebrtcPlaybackTile()) {
      this.onWebrtcSkip(deltaSeconds);
      return;
    }

    if (this.usesAlarmImageClip()) {
      const next = normalizeAlarmClipElapsedSeconds(
        this.previewElapsedSeconds() + deltaSeconds,
        this.alarmClipCycleSeconds(),
      );
      this.previewElapsedSeconds.set(next);
      if (this.previewPlaying()) {
        this.startAlarmImageClipTicker();
      }
      return;
    }

    const video = this.previewVideoRef()?.nativeElement;
    if (!video || this.useLibraryTimeline()) {
      return;
    }

    const duration =
      Number.isFinite(video.duration) && video.duration > 0
        ? video.duration
        : this.previewTimelineDurationSeconds();
    const next = Math.min(duration, Math.max(0, video.currentTime + deltaSeconds));
    try {
      video.currentTime = next;
    } catch {
      // jsdom / missing media APIs
    }
    this.previewElapsedSeconds.set(next);
  }

  onLibraryCenterDateApply(date: Date): void {
    const player = this.resolveLibraryPlayer();
    if (!player) {
      return;
    }

    const instant = fromNvrWallClockDate(date, this.playbackCameraTimeZone());
    const target = this.clampLibraryStreamTime(instant, player);
    this.libraryWindowCenter.set(target);
    this.libraryCenterDate.set(target);
    player.tickerTimeZone = resolvePlaybackTimeZone(this.playbackCameraTimeZone());
    player.pinnedPlayhead = target;
    player.currentTickerTime = target;
    const range = this.resolveNativeLibraryTimelineRange();
    this.applyLibraryWindowRange(player, range);
    this.withoutLibraryStreamRestart(() => player.rebuildTimeline?.());
    this.pinLibraryPlayhead(player, target);
    this.pauseLibraryAfterSeek = this.libraryPlaybackPaused;
    if (this.pauseLibraryAfterSeek) {
      // The new session has to open. Pause runs again after its first frame.
      this.libraryPlaybackPaused = false;
    }
    this.reinitializeWebrtcAt(target);
    this.reloadHistoricTimelineAlarms();
    this.refreshLibraryEventIndex(player);
  }

  /** Steps to the next speed, wrapping from the fastest back to 1X. */
  onLibrarySpeedStep(): void {
    const current = this.librarySpeedOptions.findIndex(
      (option) => option.id === this.librarySpeed(),
    );
    const next = this.librarySpeedOptions[(current + 1) % this.librarySpeedOptions.length];
    this.onLibrarySpeedChange(next.id);
  }

  onLibrarySpeedChange(speedId: string): void {
    const speed = Number(speedId);
    if (!Number.isFinite(speed) || speed <= 0) {
      return;
    }
    const playhead = this.resolveLibrarySpeedPlayhead();
    if (this.isLiveLibrarySpeedPlayhead(playhead) && speed !== 1) {
      this.librarySpeed.set('1');
      this.resolveLibraryPlayer()?.onSpeedChange?.(1);
      this.applyLibraryPlaybackRate();
      return;
    }
    this.librarySpeed.set(speedId);
    this.resolveLibraryPlayer()?.onSpeedChange?.(speed);
    this.applyLibraryPlaybackRate();
    if (playhead && this.useLibraryTimeline() && playhead.getTime() < Date.now() - 1_000) {
      this.reinitializeWebrtcAt(playhead);
    }
  }

  /**
   * Push the chrome speed onto whichever element is on screen. A live WHEP
   * session is a MediaStream, where the browser ignores `playbackRate`; the
   * rate lands once playback is file-backed.
   */
  private applyLibraryPlaybackRate(): void {
    const rate = Number(this.librarySpeed());
    if (!Number.isFinite(rate) || rate <= 0) {
      return;
    }
    const stage = this.playbackPage()?.stage as LibraryStageInstance | undefined;
    const videos = [
      this.previewVideoRef()?.nativeElement,
      stage?.videoPlayers?.first?.nativeElement,
    ];
    for (const video of videos) {
      if (!video) {
        continue;
      }
      try {
        video.playbackRate = rate;
      } catch {
        // jsdom / missing media APIs
      }
    }
  }

  onLibraryAiEventsChange(values: string[]): void {
    const ordered = PLAYBACK_MOTION_OPTIONS.map((option) => option.value).filter((value) =>
      values.includes(value),
    ) as EventAiTagVariant[];
    this.libraryAiEventValues.set(ordered);
    this.visibleDetectionTypesChange.emit(ordered);
    this.rematerializeHistoricAlarmSegments();
    this.applyLibraryMotionFilter();

    const selected = this.playbackService.resolveCameraById(this.cameras(), this.cameraId());
    if (!selected || this.mode() !== 'playback' || this.shouldUseWebrtcPreview()) {
      return;
    }
    if (this.strictVisibleDetectionTypes() && !ordered.length) {
      this.detectionBoxes.set([]);
      return;
    }
    void this.loadDetections(selected, ordered, this.playbackWindow());
  }

  onPreviewLoaded(event: Event): void {
    const video = event.target as HTMLVideoElement;
    this.previewStreamConnecting.set(false);
    this.previewStreamUnavailable.set(false);
    if (video.videoWidth > 0 && video.videoHeight > 0) {
      this.videoIntrinsicSize.set({ width: video.videoWidth, height: video.videoHeight });
    }
    this.previewMediaDurationSeconds.set(
      Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0,
    );
    if (this.pauseLibraryAfterSeek) {
      this.pauseLibraryAfterSeek = false;
      this.pauseLibraryTimeline();
      const player = this.resolveLibraryPlayer();
      if (player) {
        player.isPlaying = false;
      }
      this.emitAlarmClipViewedIfEligible();
      return;
    }
    if (this.previewUsesWhep()) {
      this.previewPlaying.set(true);
      void video.play()?.catch(() => {
        this.previewPlaying.set(false);
      });
      this.emitAlarmClipViewedIfEligible();
      return;
    }
    this.applyPreviewStartPosition(video);
    this.previewPlaying.set(true);
    void video.play()?.catch(() => {
      this.previewPlaying.set(false);
    });
    this.emitAlarmClipViewedIfEligible();
  }

  onAlarmClipImageLoaded(event: Event): void {
    const image = event.target as HTMLImageElement;
    if (image.naturalWidth > 0 && image.naturalHeight > 0) {
      this.videoIntrinsicSize.set({
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
    }
    this.alarmClipImagesFailed.set(false);
    this.previewStreamConnecting.set(false);
    this.previewStreamUnavailable.set(false);
    this.commitShownAlarmClipFrame(image);
    if (this.usesAlarmImageClip()) {
      const camera = this.playbackService.resolveCameraById(this.cameras(), this.cameraId());
      if (camera) {
        void this.loadDetections(camera, this.visibleDetectionTypes(), this.playbackWindow());
      }
      if (this.previewPlaying() && this.alarmImageClipRafId === null) {
        this.startAlarmImageClipTicker();
      }
    }
    this.emitAlarmClipViewedIfEligible();
  }

  onAlarmClipImageError(): void {
    this.markAlarmClipUnavailable();
  }

  private emitAlarmClipViewedIfEligible(): void {
    if (this.mode() !== 'playback') {
      return;
    }
    const alarmId = this.selectedAlarmId()?.trim();
    if (!alarmId) {
      return;
    }
    this.alarmClipViewed.emit(alarmId);
  }

  onPreviewPlay(): void {
    if (this.libraryPlaybackPaused) {
      return;
    }
    this.previewPlaying.set(true);
    this.adoptLibraryTransportPlaying(true);
  }

  onPreviewPlaying(): void {
    this.previewStreamConnecting.set(false);
    this.previewStreamUnavailable.set(false);
    if (this.libraryPlaybackPaused) {
      return;
    }
    this.previewPlaying.set(true);
    this.adoptLibraryTransportPlaying(true);
  }

  onPreviewPause(): void {
    const video = this.previewVideoRef()?.nativeElement;
    // srcObject assignment and an interrupted play() both emit pause while the
    // element keeps going. Only a video that actually stopped changes the icon.
    if (video && !video.paused) {
      return;
    }
    this.previewPlaying.set(false);
    this.adoptLibraryTransportPlaying(false);
  }

  /**
   * Copy the visible preview onto Caliber's transport icon.
   * Does not call onPlay() — that force-reloads the stage.
   * The player is not OnPush, but it sits under the playback page, so the host
   * check does not repaint the icon. Refresh that view directly.
   */
  private adoptLibraryTransportPlaying(playing: boolean): void {
    if (!this.useLibraryTimeline()) {
      return;
    }
    const host = this.stageWrapRef()?.nativeElement.querySelector('lib-player');
    if (!host) {
      return;
    }
    const player = resolveComponentFromHostElement<LibraryPlayerInstance>(host);
    if (!player || typeof player.rebuildTimeline !== 'function' || player.isPlaying === playing) {
      return;
    }
    player.isPlaying = playing;
    getDebugNode(host)?.injector.get(ChangeDetectorRef).detectChanges();
  }

  onPreviewTimeUpdate(event: Event): void {
    const video = event.target as HTMLVideoElement;
    if (video.currentTime > 0) {
      this.previewStreamConnecting.set(false);
    }

    if (this.mode() !== 'playback' || this.useLibraryTimeline()) {
      return;
    }

    // WHEP MediaStreams do not keep a reliable currentTime — wall-clock ticker owns elapsed.
    if (this.isWebrtcPlaybackTile()) {
      return;
    }

    // Keep playing until the user pauses — do not loop or auto-stop at the window end.
    this.previewElapsedSeconds.set(video.currentTime);
  }

  onTogglePreviewPlay(): void {
    if (this.usesAlarmImageClip()) {
      if (this.previewPlaying()) {
        this.previewPlaying.set(false);
        this.stopAlarmImageClipTicker();
        return;
      }

      this.previewPlaying.set(true);
      this.startAlarmImageClipTicker();
      return;
    }

    if (this.isWebrtcPlaybackTile() && !this.useLibraryTimeline()) {
      if (this.previewPlaying()) {
        this.pauseWebrtcPlayback();
      } else {
        this.resumeWebrtcPlayback();
      }
      return;
    }

    const video = this.previewVideoRef()?.nativeElement;
    if (!video) {
      return;
    }

    if (video.paused) {
      void video.play()?.catch(() => undefined);
    } else {
      video.pause();
    }
  }

  /**
   * A WHEP MediaStream has no buffer, so a paused `<video>` keeps receiving frames
   * and resumes wherever the stream now is. Closing the session is the only pause.
   */
  private pauseWebrtcPlayback(): void {
    const instant = this.currentPlaybackInstant();
    this.webrtcPausedAtMs = instant ? new Date(instant).getTime() : null;
    this.previewPlaying.set(false);
    this.stopWebrtcElapsedTicker();

    const video = this.previewVideoRef()?.nativeElement;
    video?.pause();
    // Closing the session clears `srcObject`, so the last frame has to be kept
    // as a still before teardown — otherwise the tile pauses to black.
    const frame = this.captureWebrtcPausedFrame(video);
    this.pausedFrameUrl.set(frame);
    if (frame) {
      this.disconnectActiveWebrtcStream();
    }
  }

  /**
   * Playback-tab Pause. A WHEP session keeps painting after `video.pause()`, so the
   * session closes only when a real still can take its place — or while it is still
   * connecting and there is no frame to lose.
   */
  private pauseLibraryTimeline(): void {
    if (this.libraryPlaybackPaused || !this.shouldUseWebrtcPreview()) {
      return;
    }
    this.previewPlaying.set(false);
    this.adoptLibraryTransportPlaying(false);
    this.stopWebrtcElapsedTicker();

    if (this.previewStreamConnecting()) {
      this.pausedFrameUrl.set(null);
      this.previewStreamConnecting.set(false);
      this.libraryPlaybackPaused = true;
      this.disconnectActiveWebrtcStream();
      return;
    }

    const video = this.previewVideoRef()?.nativeElement;
    const frame = this.captureVisiblePausedFrame(video);
    if (!frame) {
      this.pausedFrameUrl.set(null);
      return;
    }

    video?.pause();
    this.pausedFrameUrl.set(frame);
    this.libraryPlaybackPaused = true;
    this.disconnectActiveWebrtcStream();
  }

  /** Reopens the Playback-tab session at the timeline playhead. */
  private resumeLibraryTimeline(player: LibraryPlayerInstance): void {
    this.libraryPlaybackPaused = false;
    this.previewPlaying.set(true);
    const playhead = player.currentTickerTime;
    if (!(playhead instanceof Date) || Number.isNaN(playhead.getTime())) {
      this.pausedFrameUrl.set(null);
      return;
    }
    this.reinitializeWebrtcAt(playhead);
  }

  /** `null` when the still would be empty — the session then stays up. */
  private captureVisiblePausedFrame(video: HTMLVideoElement | undefined): string | null {
    if (!video) {
      return null;
    }
    try {
      const payload = captureVideoFrameAsPngBase64(video);
      if (!payload) {
        return null;
      }
      return `data:image/png;base64,${payload}`;
    } catch {
      return null;
    }
  }

  /** `null` when there is no grabbable frame — the session then stays up. */
  private captureWebrtcPausedFrame(video: HTMLVideoElement | undefined): string | null {
    if (!video) {
      return null;
    }
    try {
      return `data:image/png;base64,${captureVideoFrameAsPngBase64(video)}`;
    } catch {
      return null;
    }
  }

  /** Reopens a session at the paused instant so no footage is skipped. */
  private resumeWebrtcPlayback(): void {
    const resumeMs = this.webrtcPausedAtMs;
    this.webrtcPausedAtMs = null;
    this.previewPlaying.set(true);

    if (resumeMs === null || !Number.isFinite(resumeMs)) {
      this.pausedFrameUrl.set(null);
      void this.previewVideoRef()?.nativeElement.play()?.catch(() => undefined);
      return;
    }

    // The restart replays from its own start; keep the transport counting up.
    this.pendingWebrtcElapsedSeconds = Math.max(0, this.previewElapsedSeconds());
    this.reinitializeWebrtcAt(new Date(resumeMs));
  }

  /** Scrub the tiled / minimal preview from mini-playback-controls (ratio 0…1). */
  onPreviewSeek(ratio: number): void {
    if (this.useLibraryTimeline()) {
      return;
    }

    if (this.usesAlarmImageClip()) {
      const duration = this.alarmClipCycleSeconds();
      const target = Math.min(duration, Math.max(0, ratio * duration));
      this.previewElapsedSeconds.set(target);
      if (this.previewPlaying()) {
        this.startAlarmImageClipTicker();
      }
      return;
    }

    if (this.isWebrtcPlaybackTile()) {
      const durationSeconds = this.previewTimelineDurationSeconds();
      if (durationSeconds <= 0) {
        return;
      }
      this.previewElapsedSeconds.set(Math.min(durationSeconds, Math.max(0, ratio * durationSeconds)));
      return;
    }

    const video = this.previewVideoRef()?.nativeElement;
    if (!video) {
      return;
    }

    const timelineDuration = this.previewTimelineDurationSeconds();
    const mediaDuration =
      Number.isFinite(video.duration) && video.duration > 0
        ? video.duration
        : this.previewMediaDurationSeconds();
    const duration = timelineDuration > 0 ? timelineDuration : mediaDuration;
    if (duration <= 0) {
      return;
    }

    const max = mediaDuration > 0 ? Math.min(duration, mediaDuration) : duration;
    const target = Math.min(max, Math.max(0, ratio * duration));
    try {
      video.currentTime = target;
    } catch {
      // jsdom / missing media APIs
    }
    this.previewElapsedSeconds.set(target);
  }

  onFlagClick(): void {
    this.browserTabs.updateActivePageState({
      playbackFlagReportOpen: true,
      playbackFlagReportCameraId: this.cameraId(),
    });
  }

  onFlagReportClose(): void {
    this.browserTabs.updateActivePageState({
      playbackFlagReportOpen: false,
      playbackFlagReportCameraId: null,
      playbackSelectedReportReasonId: null,
    });
  }

  onFlagReportReasonSelect(reasonId: CameraReportReasonId): void {
    this.browserTabs.updateActivePageState({ playbackSelectedReportReasonId: reasonId });
  }

  onSnapshotClick(): void {
    if (!this.snapshotEnabled() || this.submittingSnapshot()) {
      return;
    }

    const camera = this.playbackService.resolveCameraById(this.cameras(), this.cameraId());
    if (!camera) {
      return;
    }

    this.submittingSnapshot.set(true);
    void this.submitSnapshot(camera.name);
  }

  private async submitSnapshot(cameraName: string): Promise<void> {
    try {
      const capturedAt = this.snapshotCapturedAtIso();
      const imageBase64 = await this.captureSnapshotFrame();
      await firstValueFrom(
        this.playbackService.captureSnapshot(
          this.eventId(),
          buildSnapshotAssetRequest(imageBase64, cameraName, capturedAt),
        ),
      );
      this.toast.snapshotCaptured(cameraName);
    } catch (error: unknown) {
      if (isSnapshotImageLimitReached(error)) {
        this.toast.show('snapshot-failed', {
          title: 'Limit for snapshot is reached',
          tone: 'error',
        });
      } else {
        this.toast.show('snapshot-failed', {
          title: 'Snapshot failed',
          tone: 'error',
        });
      }
    } finally {
      this.submittingSnapshot.set(false);
    }
  }

  onAttachAlarmClipClick(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    void this.submitAttachAlarmClip();
  }

  private async submitAttachAlarmClip(): Promise<void> {
    if (!this.showAttachAlarmClip() || this.submittingAttachAlarmClip()) {
      return;
    }

    const eventId = this.eventId().trim();
    const alarmId = this.selectedAlarmId()?.trim() ?? '';
    if (!eventId || !alarmId) {
      this.toast.show('alarm-clip-attach-failed', {
        subtitle: 'Invalid alarm details, unable to add clip.',
      });
      return;
    }

    const camera = this.playbackService.resolveCameraById(this.cameras(), this.cameraId());
    const cameraName = camera?.name.trim() || this.headerTitle().trim() || 'Camera';

    this.submittingAttachAlarmClip.set(true);
    try {
      await firstValueFrom(
        this.playbackService.attachAlarmClip(
          eventId,
          buildClipAssetRequest(alarmId, cameraName, this.clipCapturedAtIso()),
        ),
      );
      this.toast.alarmClipAttached(cameraName);
    } catch (error) {
      const status = (error as any)?.status;
      const subtitle = status === 409
        ?' Alarm Clip already exists in Resolve.'
        : 'Unexpected error, unable to add clip.';
      this.toast.show('alarm-clip-attach-failed', { subtitle });
    } finally {
      this.submittingAttachAlarmClip.set(false);
    }
  }

  private resolveSelectedVideo(): HTMLVideoElement {
    const video = this.resolveLivePlayerElement();
    if (!video) {
      throw new Error('no-playable-frame');
    }
    return video;
  }

  private async fetchAlarmClipFrameBlob(url: string): Promise<Blob> {
    const proxied = resolveSameOriginMediaFetchUrl(url);
    if (proxied !== url) {
      try {
        const blob = await firstValueFrom(this.http.get(proxied, { responseType: 'blob' }));
        if (blob.size && blob.type.startsWith('image/')) {
          return blob;
        }
      } catch {
        // Local /__blob proxy may be missing until ng serve restarts.
      }
    }

    return firstValueFrom(this.http.get(url, { responseType: 'blob' }));
  }

  /**
   * NVR clock when the alarm clip starts. Gallery hover shows these UTC fields
   * as the clip time, so a 02:19:35 MST clip is `...T02:19:35.000Z`.
   */
  private clipCapturedAtIso(): string | null {
    const start = this.alarmClipStartInstant();
    if (!start) {
      return null;
    }
    return nvrWallClockAsUtcIso(start, this.playbackCameraTimeZone());
  }

  /**
   * NVR clock of the frame being saved. Gallery hover shows these UTC fields
   * as the capture time, so a New York 22:57:35 frame is `...T22:57:35.000Z`.
   */
  private snapshotCapturedAtIso(): string {
    return nvrWallClockAsUtcIso(this.snapshotCaptureInstant(), this.playbackCameraTimeZone());
  }

  /** UTC instant of the visible frame. Live tiles use the current time. */
  private snapshotCaptureInstant(): Date {
    if (this.useLibraryTimeline()) {
      const playhead = this.libraryCenterDate();
      if (playhead && !Number.isNaN(playhead.getTime())) {
        return playhead;
      }
    }

    const playbackInstant = this.currentPlaybackInstant();
    if (playbackInstant) {
      const parsed = new Date(playbackInstant);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed;
      }
    }

    if (this.usesAlarmImageClip()) {
      const start = this.alarmClipStartInstant() ?? this.playbackWindow()?.start ?? null;
      const frameCount = this.alarmClipFrameUrls().length || this.alarmClipImageSources().length;
      if (start && frameCount > 0) {
        const index = resolveAlarmClipFrameIndex(this.previewElapsedSeconds(), frameCount);
        return new Date(start.getTime() + index * ALARM_CLIP_SECONDS_PER_FRAME * 1000);
      }
    }

    if (this.mode() === 'playback' && !this.isWebrtcPlaybackTile()) {
      const window = this.playbackWindow();
      if (window) {
        return new Date(
          window.start.getTime() + Math.max(0, this.previewElapsedSeconds()) * 1000,
        );
      }
    }

    return new Date();
  }

  /**
   * Alarm `createdDateTime` for the open clip. ISO values stay absolute;
   * display strings such as `10/14/2026 03:03:41 CDT` still parse as written.
   */
  private alarmClipStartInstant(): Date | null {
    const raw = this.alarmTimestamp()?.trim();
    if (!raw) {
      return null;
    }
    if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) {
      const parsed = parseUtcInstant(raw);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return parseEventAlarmTimestamp(raw);
  }

  private async captureSnapshotFrame(): Promise<string> {
    if (this.usesAlarmImageClip()) {
      const url = this.activeAlarmClipFrameUrl();
      if (!url) {
        throw new Error('no-playable-frame');
      }
      if (url.startsWith('blob:')) {
        const image = this.previewImageRef()?.nativeElement;
        if (!image) {
          throw new Error('no-playable-frame');
        }
        return captureImageElementAsPngBase64(image);
      }

      const blob = await this.fetchAlarmClipFrameBlob(url);
      return captureFetchedImageAsPngBase64(blob);
    }

    return captureVideoFrameAsPngBase64(this.resolveSelectedVideo());
  }

  private startAlarmImageClipTicker(): void {
    this.stopAlarmImageClipTicker();
    if (!this.usesAlarmImageClip() || !this.previewPlaying()) {
      return;
    }

    this.alarmImageClipLastTickMs = performance.now();
    const tick = (now: number) => {
      if (!this.previewPlaying() || !this.usesAlarmImageClip()) {
        this.stopAlarmImageClipTicker();
        return;
      }

      const last = this.alarmImageClipLastTickMs ?? now;
      const deltaSeconds = (now - last) / 1000;
      this.alarmImageClipLastTickMs = now;
      this.previewElapsedSeconds.set(
        normalizeAlarmClipElapsedSeconds(
          this.previewElapsedSeconds() + deltaSeconds,
          this.alarmClipCycleSeconds(),
        ),
      );
      this.alarmImageClipRafId = requestAnimationFrame(tick);
    };

    this.alarmImageClipRafId = requestAnimationFrame(tick);
  }

  private stopAlarmImageClipTicker(): void {
    if (this.alarmImageClipRafId !== null) {
      cancelAnimationFrame(this.alarmImageClipRafId);
      this.alarmImageClipRafId = null;
    }
    this.alarmImageClipLastTickMs = null;
  }

  private alarmClipCycleSeconds(): number {
    return alarmClipDurationSeconds(
      this.alarmClipFrameUrls().length || this.alarmClipImageSources().length,
    );
  }

  /** Selected fullscreen rate. 1 until a faster stream is playing. */
  private libraryPlaybackRate(): number {
    const rate = Number(this.librarySpeed());
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  }

  private shouldRunLibraryPlayheadTicker(): boolean {
    return (
      this.useLibraryTimeline() &&
      this.previewPlaying() &&
      !this.previewStreamConnecting() &&
      !this.previewStreamUnavailable() &&
      !this.libraryPlaybackPaused
    );
  }

  /**
   * Move the centered playhead with the stream. WHEP has no `currentTime`, so
   * elapsed wall clock is scaled by the selected rate (1x, 1.5x, or 2x).
   */
  private startLibraryPlayheadTicker(): void {
    this.stopLibraryPlayheadTicker();
    const player = this.resolveLibraryPlayer();
    if (!this.shouldRunLibraryPlayheadTicker() || !player?.currentTickerTime) {
      return;
    }
    if (Number.isNaN(player.currentTickerTime.getTime())) {
      return;
    }
    player.pinnedPlayhead = null;
    const originMs = player.currentTickerTime.getTime();
    const originPerf = performance.now();
    const tick = (now: number): void => {
      if (!this.shouldRunLibraryPlayheadTicker()) {
        this.stopLibraryPlayheadTicker();
        return;
      }
      const current = this.resolveLibraryPlayer();
      if (!current) {
        this.stopLibraryPlayheadTicker();
        return;
      }
      // A calendar or drag seek owns the handle until that session starts playing.
      if (current.pinnedPlayhead instanceof Date && !Number.isNaN(current.pinnedPlayhead.getTime())) {
        this.stopLibraryPlayheadTicker();
        return;
      }
      const elapsed = Math.max(0, now - originPerf) * this.libraryPlaybackRate();
      const next = new Date(Math.min(Date.now(), originMs + elapsed));
      current.pinnedPlayhead = null;
      current.currentTickerTime = next;
      this.libraryCenterDate.set(next);
      this.centerLibraryViewportOn(current, next);
      this.libraryPlayheadRafId = requestAnimationFrame(tick);
    };
    this.libraryPlayheadRafId = requestAnimationFrame(tick);
  }

  private stopLibraryPlayheadTicker(): void {
    if (this.libraryPlayheadRafId !== null) {
      cancelAnimationFrame(this.libraryPlayheadRafId);
      this.libraryPlayheadRafId = null;
    }
  }

  private startWebrtcElapsedTicker(): void {
    this.stopWebrtcElapsedTicker();
    if (
      !this.isWebrtcPlaybackTile() ||
      !this.previewPlaying() ||
      this.previewStreamConnecting() ||
      this.previewStreamUnavailable() ||
      this.useLibraryTimeline()
    ) {
      return;
    }

    this.webrtcElapsedLastTickMs = performance.now();
    const tick = (now: number) => {
      if (
        !this.previewPlaying() ||
        !this.isWebrtcPlaybackTile() ||
        this.previewStreamConnecting() ||
        this.previewStreamUnavailable() ||
        this.useLibraryTimeline()
      ) {
        this.stopWebrtcElapsedTicker();
        return;
      }

      const last = this.webrtcElapsedLastTickMs ?? now;
      this.webrtcElapsedLastTickMs = now;
      const duration = this.previewTimelineDurationSeconds();
      const next = this.previewElapsedSeconds() + (now - last) / 1000;
      if (duration > 0 && next >= duration) {
        this.previewElapsedSeconds.set(duration);
        this.pauseWebrtcPlayback();
        return;
      }
      this.previewElapsedSeconds.set(next);
      this.webrtcElapsedRafId = requestAnimationFrame(tick);
    };

    this.webrtcElapsedRafId = requestAnimationFrame(tick);
  }

  private stopWebrtcElapsedTicker(): void {
    if (this.webrtcElapsedRafId !== null) {
      cancelAnimationFrame(this.webrtcElapsedRafId);
      this.webrtcElapsedRafId = null;
    }
    this.webrtcElapsedLastTickMs = null;
  }

  private async hydrateAlarmClipFrameUrls(sources: readonly string[]): Promise<void> {
    const seq = ++this.alarmClipLoadSeq;
    this.alarmClipImagesFailed.set(false);

    const loaded = await Promise.all(
      sources.map((source) => this.loadAlarmClipDisplayUrl(source)),
    );
    if (seq !== this.alarmClipLoadSeq) {
      this.revokeBlobUrls(loaded);
      return;
    }

    const firstFrame = loaded[0];
    if (!firstFrame) {
      this.revokeBlobUrls(loaded);
      this.markAlarmClipUnavailable();
      return;
    }

    const kept: { url: string; index: number }[] = [];
    loaded.forEach((url, index) => {
      if (url) {
        kept.push({ url, index });
        return;
      }
    });

    this.revokeAlarmClipObjectUrls();
    this.alarmClipObjectUrls = kept
      .map((frame) => frame.url)
      .filter((url) => url.startsWith('blob:'));
    this.alarmClipSourceFrameIndexes.set(kept.map((frame) => frame.index));
    this.alarmClipShownFrameIndex.set(0);
    this.alarmClipFrameUrls.set(kept.map((frame) => frame.url));
    this.previewStreamConnecting.set(false);
    this.previewStreamUnavailable.set(false);
  }

  /**
   * Download one still before playback. A failure returns null so a later
   * miss can be dropped without discarding the rest of the clip.
   */
  private async loadAlarmClipDisplayUrl(source: string): Promise<string | null> {
    if (requiresAuthenticatedMediaFetch(source)) {
      try {
        const blob = await firstValueFrom(this.http.get(source, { responseType: 'blob' }));
        const objectUrl = URL.createObjectURL(blob);
        const decoded = await this.verifyAlarmClipImageUrl(objectUrl);
        if (!decoded) {
          URL.revokeObjectURL(objectUrl);
          return null;
        }
        return objectUrl;
      } catch {
        return null;
      }
    }

    return (await this.verifyAlarmClipImageUrl(source)) ? source : null;
  }

  private revokeBlobUrls(urls: readonly (string | null)[]): void {
    for (const url of urls) {
      if (url?.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    }
  }

  private verifyAlarmClipImageUrl(url: string): Promise<boolean> {
    if (requiresAuthenticatedMediaFetch(url)) {
      return firstValueFrom(this.http.get(url, { responseType: 'blob' }))
        .then(() => true)
        .catch(() => false);
    }

    return new Promise((resolve) => {
      const probe = new Image();
      probe.onload = () => resolve(probe.naturalWidth > 0 && probe.naturalHeight > 0);
      probe.onerror = () => resolve(false);
      probe.src = url;
    });
  }

  private markAlarmClipUnavailable(): void {
    this.alarmClipImagesFailed.set(true);
    this.previewStreamConnecting.set(false);
    this.previewStreamUnavailable.set(false);
    this.previewPlaying.set(false);
    this.detectionBoxes.set([]);
    this.stopAlarmImageClipTicker();
    this.revokeAlarmClipObjectUrls();
    this.alarmClipSourceFrameIndexes.set([]);
    this.alarmClipShownFrameIndex.set(0);
    this.alarmClipFrameUrls.set([]);
  }

  private revokeAlarmClipObjectUrls(): void {
    for (const url of this.alarmClipObjectUrls) {
      URL.revokeObjectURL(url);
    }
    this.alarmClipObjectUrls = [];
  }

  private clearAlarmClipFrameUrls(): void {
    this.alarmClipLoadSeq += 1;
    this.alarmClipImagesFailed.set(false);
    this.revokeAlarmClipObjectUrls();
    this.alarmClipSourceFrameIndexes.set([]);
    this.alarmClipShownFrameIndex.set(0);
    this.alarmClipFrameUrls.set([]);
  }

  /** Boxes stay on this still until its pixels have loaded. */
  private commitShownAlarmClipFrame(image: HTMLImageElement): void {
    const urls = this.alarmClipFrameUrls();
    const loaded = [image.getAttribute('src'), image.currentSrc, image.src].filter(
      (value): value is string => !!value,
    );
    const index = urls.findIndex((url) =>
      loaded.some((candidate) => candidate === url || candidate.endsWith(url)),
    );
    if (index >= 0) {
      this.alarmClipShownFrameIndex.set(index);
    }
  }

  onDetectionBoxClick(event: EventqDetectionBoxClick): void {
    if (!this.aiFeedbackEnabled()) {
      return;
    }
    const detectionId = event.box.id ?? `det-${event.box.eventType}-${event.box.offsetSeconds ?? 0}`;
    this.selectedAiFeedbackBox.set(event.box as DetectionBoundingBox);
    this.aiFeedbackAnchor.set(event.anchorElement);
    this.openAiFeedbackState(detectionId, event.box.eventType);
  }

  onAiFeedbackClose(): void {
    this.aiFeedbackAnchor.set(null);
    this.selectedAiFeedbackBox.set(null);
    this.clearAiFeedbackState();
  }

  onAiFeedbackConfirm(event: { detectionId: string; variant: AiFeedbackVariant }): void {
    const camera = this.playbackService.resolveCameraById(this.cameras(), this.cameraId());
    const box = this.selectedAiFeedbackBox();
    if (!camera || !box) {
      return;
    }

    this.submittingAiFeedback.set(true);
    this.playbackService
      .submitAiFeedback(this.eventId(), event.detectionId, {
        feedback: {
          variant: event.variant,
          falsePositive: true,
        },
        detection: {
          id: event.detectionId,
          eventType: event.variant,
          box: {
            x: box.x,
            y: box.y,
            width: box.width,
            height: box.height,
            offsetSeconds: box.offsetSeconds ?? 0,
          },
        },
        camera: {
          cameraId: camera.id,
          numericId: camera.numericId,
          name: camera.name,
        },
        event: {
          eventId: this.eventId(),
        },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.submittingAiFeedback.set(false);
          this.toast.aiFeedbackReceived();
          this.onAiFeedbackClose();
        },
        error: () => {
          this.submittingAiFeedback.set(false);
          this.toast.aiFeedbackFailed();
        },
      });
  }

  onFlagReportConfirm(event: { reasonId: CameraReportReasonId }): void {
    const camera = this.playbackService.resolveCameraById(this.cameras(), this.cameraId());
    if (!camera) {
      return;
    }

    const option = CAMERA_REPORT_REASON_OPTIONS.find((entry) => entry.id === event.reasonId);
    if (!option) {
      return;
    }

    const siteId = this.siteId();
    const siteName = this.siteName().trim();
    const site =
      siteId != null && siteId > 0 && siteName ? { siteId, siteName } : undefined;

    this.submittingReport.set(true);
    this.playbackService
      .submitCameraReport(
        this.eventId(),
        camera.id,
        {
          report: {
            reasonId: event.reasonId,
            reasonLabel: option.label.replace(/\n/g, ' '),
          },
          camera: {
            cameraId: camera.id,
            numericId: camera.numericId,
            name: camera.name,
            nvrId: camera.nvrId ?? null,
            nvrName: camera.nvrName ?? null,
          },
          event: {
            eventId: this.eventId(),
          },
        },
        site,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.submittingReport.set(false);
          this.toast.cameraIssueReported(
            camera.name,
            option.label.replace(/\n/g, ' '),
          );
          this.onFlagReportClose();
        },
        error: (error) => {
          const status = error?.status;
          const options =
            status === 409
              ? {
                  title: 'Report exists',
                  subtitle: 'Camera case already exists and will be investigated.',
                }
              : {
                  title: 'Report failed',
                  subtitle: 'Unexpected error, could not create the camera case.',
                };
          this.submittingReport.set(false);
          this.toast.show('notice', { ...options, tone: 'error' });
        },
      });
  }

  private resolveAnchorDate(): Date | null {
    const iso = this.playbackAnchorTimestamp();
    if (iso) {
      const parsed = new Date(iso);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed;
      }
    }
    return parseEventAlarmTimestamp(this.headerTimestamp());
  }

  private resolvePlaybackWindow(): PlaybackWindow | null {
    if (this.mode() !== 'playback') {
      return null;
    }

    const anchor = this.resolveAnchorDate();
    if (!anchor) {
      return null;
    }

    return computePlaybackWindow(
      anchor,
      this.effectivePlaybackViewMode(),
      this.playbackCustomRangeSeconds(),
      this.playbackAnchorMode(),
    );
  }

  /** Playback-tab grid tiles use the camera substream via initialize + WHEP. */
  private shouldUseWebrtcPreview(): boolean {
    return this.isWebrtcPlaybackTile();
  }

  /** Fullscreen / library timeline uses rtspStream1; the grid tile uses rtspStream2. */
  private resolveActiveStreamType(): 'main' | 'sub' {
    if (this.useLibraryTimeline() || this.hostDocumentFullscreen()) {
      return 'main';
    }
    return this.streamType();
  }

  /** Playback-tab grid tiles use initialize + WHEP; pass the alarm window for segment metadata. */
  private resolveWebrtcPlaybackOptions(): FetchLiveSegmentsOptions | undefined {
    if (!this.shouldUseWebrtcPreview()) {
      return undefined;
    }
    const startMs = this.ensureWebrtcStartMs();
    const window = this.resolvePlaybackWindow();
    const options: FetchLiveSegmentsOptions = {
      startMs,
      playbackWindow: window ? { start: window.start, end: window.end } : undefined,
    };
    if (this.useLibraryTimeline() && typeof startMs === 'number') {
      const rate = Number(this.librarySpeed());
      if (rate === 1.5 || rate === 2) {
        options.playbackRate = rate;
      }
    }
    return options;
  }

  /** `undefined` until a calendar time is chosen — the tile opens live. */
  private resolveWebrtcStartMs(): number | undefined {
    const cameraId = this.cameraId();
    if (cameraId) {
      const stored = this.pageState().playbackStreamStartMsByCameraId[cameraId];
      if (typeof stored === 'number' && Number.isFinite(stored) && stored > 0) {
        return stored;
      }
    }

    // Select Date & Time for All Cameras seeds a new tile that has no local time yet.
    const anchorMs = this.resolveAnchorDate()?.getTime();

    // A skip that reached live must keep the tile live — re-seeding from the anchor
    // would throw the playhead back to the alarm instead of holding the live edge.
    if (
      this.webrtcLiveEdgeForAnchorMs !== null &&
      this.webrtcLiveEdgeForAnchorMs === (anchorMs ?? null)
    ) {
      return undefined;
    }

    if (
      typeof anchorMs === 'number' &&
      Number.isFinite(anchorMs) &&
      anchorMs > 0 &&
      anchorMs < Date.now()
    ) {
      return Math.trunc(anchorMs);
    }
    return undefined;
  }

  private ensureWebrtcStartMs(): number | undefined {
    return untracked(() => {
      const startMs = this.resolveWebrtcStartMs();
      if (typeof startMs === 'number') {
        this.persistWebrtcStartMs(startMs);
      }
      return startMs;
    });
  }

  private persistWebrtcStartMs(startMs: number): void {
    // Any explicit time wins over a previous run up to the live edge.
    this.webrtcLiveEdgeForAnchorMs = null;
    const cameraId = this.cameraId();
    if (!cameraId) {
      return;
    }
    const current = this.pageState().playbackStreamStartMsByCameraId;
    if (current[cameraId] === startMs) {
      return;
    }
    this.browserTabs.updateActivePageState({
      playbackStreamStartMsByCameraId: { ...current, [cameraId]: startMs },
    });
  }

  private clearWebrtcStartMs(): void {
    const cameraId = this.cameraId();
    if (!cameraId) {
      return;
    }
    const next = { ...this.pageState().playbackStreamStartMsByCameraId };
    if (!(cameraId in next)) {
      return;
    }
    delete next[cameraId];
    this.browserTabs.updateActivePageState({ playbackStreamStartMsByCameraId: next });
  }

  /** WHEP cannot seek, so every timeline move creates a fresh session at the target. */
  private reinitializeWebrtcAt(time: Date): void {
    if (!this.shouldUseWebrtcPreview()) {
      return;
    }
    const targetMs = time.getTime();
    if (!Number.isFinite(targetMs)) {
      return;
    }
    if (targetMs >= Date.now() - 1_000) {
      this.webrtcLiveEdgeForAnchorMs = untracked(
        () => this.resolveAnchorDate()?.getTime() ?? null,
      );
      this.clearWebrtcStartMs();
    } else {
      this.persistWebrtcStartMs(Math.max(0, Math.trunc(targetMs)));
    }
    this.pausedFrameUrl.set(null);
    this.previewStreamUnavailable.set(false);
    this.previewStreamConnecting.set(true);
    // The scrub already positioned the timeline — the restart must not move it.
    this.libraryPreserveViewOnConfigure = this.useLibraryTimeline();
    this.rebootstrapSelectedCamera();
  }

  private consumeLibraryPreservedView(): boolean {
    const preserve = this.libraryPreserveViewOnConfigure;
    this.libraryPreserveViewOnConfigure = false;
    return preserve;
  }

  private rebootstrapSelectedCamera(): void {
    const selected = this.playbackService.resolveCameraById(this.cameras(), this.cameraId());
    if (!selected) {
      return;
    }
    // A new start time is a new session: drop the old one so MediaMTX releases the
    // path, and so the tile re-attaches even when initialize repeats the URL.
    this.disconnectActiveWebrtcStream();
    // This restart already targets the new start time — keep the key from re-firing it.
    this.activeStreamSessionKey = this.resolveStreamSessionKey(selected.id);
    const bootstrapSeq = ++this.playbackBootstrapSeq;
    this.pendingWebrtcRestartSeq = bootstrapSeq;
    void this.bootstrapPlayback(selected, bootstrapSeq).finally(() => {
      if (this.pendingWebrtcRestartSeq === bootstrapSeq) {
        this.pendingWebrtcRestartSeq = null;
      }
    });
  }

  /**
   * Camera + stream type + RTSP path + mode + view + start ms + library rate.
   * WHEP cannot seek, so a new start time, path, or speed is a new session.
   * A device poll that repeats the same path is the same session.
   */
  private resolveStreamSessionKey(cameraKey: string | number): string {
    const libraryTimeline = this.useLibraryTimeline();
    const streamType = this.resolveActiveStreamType();
    const startMs = this.shouldUseWebrtcPreview()
      ? untracked(() => this.resolveWebrtcStartMs())
      : undefined;
    const camera = this.playbackService.resolveCameraById(this.cameras(), String(cameraKey));
    const parts = [
      cameraKey,
      streamType,
      camera?.streamUrls?.[streamType] ?? '',
      this.mode(),
      this.effectivePlaybackViewMode(),
      libraryTimeline ? 'library' : 'tile',
      startMs ?? 'live',
    ];
    if (libraryTimeline) {
      parts.push(untracked(() => this.librarySpeed()));
    }
    return parts.join(':');
  }

  private resolveLibrarySpeedPlayhead(): Date | null {
    const ticker = this.resolveLibraryPlayer()?.currentTickerTime;
    return ticker instanceof Date && !Number.isNaN(ticker.getTime()) ? ticker : null;
  }

  /** Live edge ignores speeds above 1×. A missing playhead is not live. */
  private isLiveLibrarySpeedPlayhead(playhead: Date | null): boolean {
    return (
      this.useLibraryTimeline() &&
      playhead instanceof Date &&
      playhead.getTime() >= Date.now() - 1_000
    );
  }

  private async bootstrapPlayback(
    camera: EventCameraItem,
    bootstrapSeq = this.playbackBootstrapSeq,
  ): Promise<boolean> {
    const isCurrent = () => bootstrapSeq === this.playbackBootstrapSeq;

    if (this.usesAlarmImageClip()) {
      const clipKey = `${this.selectedAlarmId() ?? ''}\n${this.alarmClipImageSources().join('\n')}`;
      const sameClip = clipKey === this.alarmImageClipSessionKey;
      const keepPausedFrame = sameClip && !untracked(() => this.previewPlaying());
      const keepFailedClip = sameClip && untracked(() => this.alarmClipImagesFailed());
      this.alarmImageClipSessionKey = clipKey;

      this.previewVideoUrl.set(null);
      this.previewUsesWhep.set(false);
      this.previewStreamUrl.set(null);
      this.previewStreamConnecting.set(false);
      this.previewStreamUnavailable.set(false);
      if (!keepFailedClip) {
        this.alarmClipImagesFailed.set(false);
      }
      if (keepPausedFrame) {
        this.stopAlarmImageClipTicker();
      } else {
        this.previewElapsedSeconds.set(0);
        this.previewPlaying.set(true);
      }
      this.videoIntrinsicSize.set({ width: 0, height: 0 });
      this.applyAlarmClipDetectionBoxes();
      return true;
    }

    this.alarmImageClipSessionKey = '';
    this.stopAlarmImageClipTicker();
    if (this.inAlarmsClipMode()) {
      this.previewPlaying.set(false);
    }

    if (this.mode() === 'live' || this.shouldUseWebrtcPreview()) {
      await this.awaitPendingWebrtcDisconnect();
      if (!isCurrent()) {
        return false;
      }
      let merged = await this.playbackService.fetchLiveSegmentsAndUpdate(
        camera,
        this.cameraList,
        this.updateSelectedCameras$,
        this.resolveActiveStreamType(),
        this.resolveWebrtcPlaybackOptions(),
      );
      if (!isCurrent()) {
        return false;
      }
      if (this.useLibraryTimeline()) {
        merged = this.withLibraryWebrtcArchive(merged, this.resolveNativeLibraryTimelineRange());
        this.updateSelectedCameras$.next(merged);
      }
      this.cameraList = merged;
      this.applyHistoricAlarmSegmentsToCameraList();
      this.syncPreviewVideo(camera.numericId, this.cameraList);
      this.applyWebrtcPreviewAvailability();
      if (this.useLibraryTimeline()) {
        this.configureLibraryTimeline();
        this.bindLibraryViewportLoading();
      }
      return true;
    }

    if (this.useLibraryTimeline()) {
      const range = this.resolveNativeLibraryTimelineRange();
      const anchor = this.resolveAnchorDate() ?? range.end;
      this.libraryLoadedWindow = { start: range.start.getTime(), end: range.end.getTime() };
      this.syncPreviewVideo(camera.numericId, this.cameraList);
      this.configureLibraryTimeline();
      this.bindLibraryViewportLoading();
      await this.primeStageVideo(camera.numericId, anchor, this.cameraList);
      if (!isCurrent()) {
        return false;
      }
      this.refreshLibraryDetectionBoxes(
        camera.numericId,
        this.resolveLibraryVisibleDetectionTypes(camera),
      );
      return true;
    }

    if (!isCurrent()) {
      return false;
    }
    this.syncPreviewVideo(camera.numericId, this.cameraList);
    return true;
  }

  private syncPreviewVideo(cameraId: number, cameras: PlaybackCamera[]): void {
    const camera = cameras.find((entry) => entry.id === cameraId && entry.selected);
    const segment = camera
      ? this.playbackService.pickSegmentForCamera(camera, this.visibleDetectionTypes())
      : null;
    const nextUrl = segment?.url ?? null;
    const previousUrl = this.previewVideoUrl();
    const usesWhep = isWhepPlaybackUrl(nextUrl);
    this.previewVideoUrl.set(nextUrl);
    this.previewUsesWhep.set(usesWhep);
    this.previewStreamUrl.set(usesWhep ? nextUrl : null);
    if (usesWhep && nextUrl && (nextUrl !== previousUrl || this.lastWebrtcUrl !== nextUrl)) {
      this.lastWebrtcUrl = nextUrl;
      this.previewStreamSession.update((session) => session + 1);
    }
    this.videoIntrinsicSize.set({ width: 0, height: 0 });
    if (nextUrl !== previousUrl) {
      this.previewMediaDurationSeconds.set(0);
    }
    if (!usesWhep) {
      this.previewStreamConnecting.set(false);
    } else if (nextUrl !== previousUrl) {
      this.previewStreamConnecting.set(true);
    }

    const pendingElapsed = this.pendingWebrtcElapsedSeconds;
    this.pendingWebrtcElapsedSeconds = null;
    // A skip restart keeps its carried position even when initialize hands back
    // the same URL for the new start time.
    if (usesWhep && pendingElapsed !== null) {
      this.previewElapsedSeconds.set(pendingElapsed);
      this.webrtcSessionElapsedBaseSeconds = pendingElapsed;
    } else if (!usesWhep || nextUrl !== previousUrl) {
      const startSeconds = this.resolvePreviewStartSeconds();
      this.previewElapsedSeconds.set(startSeconds);
      this.webrtcSessionElapsedBaseSeconds = startSeconds;
    }

    // Same media URL will not re-fire loadedmetadata — seek/resume in place.
    if (nextUrl && nextUrl === previousUrl && !usesWhep) {
      queueMicrotask(() => this.resumePreviewAtAlarmOffset());
    }
  }

  /** Initialize/WHEP failure: freeze the bar and show "No recording at this time". */
  private applyWebrtcPreviewAvailability(): void {
    if (this.previewUsesWhep() && this.previewStreamUrl()) {
      this.previewStreamUnavailable.set(false);
      return;
    }

    this.previewStreamConnecting.set(false);
    this.previewStreamUnavailable.set(true);
    this.previewPlaying.set(false);
    this.previewElapsedSeconds.set(0);
    this.webrtcSessionElapsedBaseSeconds = 0;
    this.stopWebrtcElapsedTicker();
  }

  /** Seeks preview to the alarm anchor inside the active clip/playback window and autoplays. */
  private applyPreviewStartPosition(video: HTMLVideoElement): void {
    const startAt = this.resolvePreviewStartSeconds();
    const max = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : startAt;
    const clamped = Math.min(startAt, Math.max(0, max - 0.05));
    try {
      video.currentTime = clamped;
    } catch {
      // A live MediaStream (and jsdom) rejects seeking — elapsed still tracks timeupdate.
    }
    this.previewElapsedSeconds.set(clamped);
  }

  private resumePreviewAtAlarmOffset(): void {
    const video = this.previewVideoRef()?.nativeElement;
    if (!video) {
      return;
    }

    this.applyPreviewStartPosition(video);
    if (this.previewPlaying() || this.mode() === 'playback') {
      this.previewPlaying.set(true);
      void video.play()?.catch(() => {
        this.previewPlaying.set(false);
      });
    }
  }

  private resolvePreviewStartSeconds(): number {
    // WHEP media is not seekable — it always plays from the start of the session.
    if (this.mode() !== 'playback' || this.previewUsesWhep()) {
      return 0;
    }

    const window = this.playbackWindow();
    if (!window) {
      return 0;
    }

    return previewStartOffsetSeconds(window, this.playbackAnchorMode());
  }

  private thisHostOwnsDocumentFullscreen(): boolean {
    const root = this.hostRootRef()?.nativeElement;
    return Boolean(root && document.fullscreenElement === root);
  }

  private async syncFullscreenState(): Promise<void> {
    const wasFullscreen = this.isFullscreen();
    const thisHostActive = this.thisHostOwnsDocumentFullscreen();
    this.hostDocumentFullscreen.set(thisHostActive);

    // Only the host that owns fullscreen writes `true`. Non-owners must not clear
    // shared tab state while another host (or sibling live/playback tile) is fullscreen.
    if (thisHostActive) {
      this.localFullscreenOwned = true;
      this.browserTabs.updateActivePageState({ playbackFullscreen: true });
      // Never drive Caliber's own fullscreen — EventQ chrome owns the FS surface.
      this.fullScreenLib$.next(false);

      if (!wasFullscreen) {
        this.attachOverlaysToFullscreenHost();
        this.adoptPortaledPanels();
        if (this.mode() === 'live' && this.streamType() === 'sub') {
          this.streamTypeChange.emit('main');
        }
        if (this.mode() === 'playback' && this.fullscreenMode() === 'library') {
          this.loadHistoricTimelineAlarms();
          const selected = this.playbackService.resolveCameraById(this.cameras(), this.cameraId());
          if (selected) {
            const bootstrapSeq = ++this.playbackBootstrapSeq;
            const completed = await this.bootstrapPlayback(selected, bootstrapSeq);
            if (!completed) {
              return;
            }
            this.bindStageTimeUpdates();
            // Wait a frame so lib-player exists, then park ticker + seed AI motion filters.
            await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
            this.configureLibraryTimeline();
            await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
            this.configureLibraryTimeline();
            this.loadHistoricTimelineAlarms();
          }
        }
      }
      return;
    }

    if (document.fullscreenElement) {
      // Another element owns document fullscreen — leave shared tab state alone.
      return;
    }

    const ownedExit = this.localFullscreenOwned;
    this.localFullscreenOwned = false;

    if (wasFullscreen) {
      this.browserTabs.updateActivePageState({ playbackFullscreen: false });
    }
    this.fullScreenLib$.next(false);

    if (ownedExit) {
      this.dismissFullscreenChrome();
    }
    // The open calendar is position:fixed and gets reparented again while the
    // fullscreen chrome is destroyed. Drop that node instead of parking it on <body>.
    this.releasePortaledPanels();

    if (this.overlayParentBeforeFullscreen) {
      this.restoreOverlaysToBody();
    }

    if (!ownedExit) {
      return;
    }

    if (this.mode() === 'playback') {
      this.unbindStageTimeUpdates();
      const selected = this.playbackService.resolveCameraById(this.cameras(), this.cameraId());
      if (selected) {
        const bootstrapSeq = ++this.playbackBootstrapSeq;
        await this.bootstrapPlayback(selected, bootstrapSeq);
        this.resumePreviewAtAlarmOffset();
      }
    }
  }

  /**
   * CDK overlays (Events mat-select, etc.) mount under body by default and are
   * invisible/unusable while a different element is fullscreen — reparent them.
   */
  private attachOverlaysToFullscreenHost(): void {
    const root = this.hostRootRef()?.nativeElement;
    if (!root) {
      return;
    }
    const overlayEl = this.overlayContainer.getContainerElement();
    this.overlayParentBeforeFullscreen = overlayEl.parentNode;
    root.appendChild(overlayEl);
  }

  /** True when the open Picture-in-Picture window is a video from this tile. */
  private ownsPictureInPicture(): boolean {
    const pip = document.pictureInPictureElement;
    if (!(pip instanceof HTMLVideoElement)) {
      return false;
    }
    return pip === this.launchedPipVideo || this.hostElement.nativeElement.contains(pip);
  }

  /**
   * Closes this tile's Picture-in-Picture window. A rejection leaves teardown free
   * to drop the stream. Invoked synchronously so it runs before disconnect.
   */
  private exitOwnedPictureInPicture(): void {
    if (!this.ownsPictureInPicture() || typeof document.exitPictureInPicture !== 'function') {
      return;
    }
    void document.exitPictureInPicture().catch(() => undefined);
  }

  private disconnectActiveWebrtcStream(): void {
    const url = this.lastWebrtcUrl;
    this.lastWebrtcUrl = null;
    // MediaMTX holds the path open while a reader is attached, so close the peer
    // connection before the server is asked to drop the session.
    const release = this.releaseActiveWhepReader;
    this.releaseActiveWhepReader = null;
    release?.();
    if (!url) {
      return;
    }
    this.pendingWebrtcDisconnect = this.streaming.disconnectWebrtcUrl(url);
  }

  /**
   * MediaMTX hands back the still-open path when initialize beats the teardown,
   * which resumes the old position instead of the newly requested start time.
   */
  private async awaitPendingWebrtcDisconnect(): Promise<void> {
    const pending = this.pendingWebrtcDisconnect;
    if (!pending) {
      return;
    }
    await pending;
    if (this.pendingWebrtcDisconnect === pending) {
      this.pendingWebrtcDisconnect = null;
    }
  }

  private restoreOverlaysToBody(): void {
    const overlayEl = this.overlayContainer.getContainerElement();
    const parent = this.overlayParentBeforeFullscreen ?? document.body;
    if (overlayEl.parentNode !== parent) {
      parent.appendChild(overlayEl);
    }
    this.overlayParentBeforeFullscreen = null;
  }

  /**
   * Shared chrome controls (`eventq-select`, `eventq-datetime-picker`) move their
   * open panel under `<body>` so it escapes clipping. A fullscreen element only
   * paints its own subtree, so those panels have to follow the host in and out.
   */
  private adoptPortaledPanels(): void {
    const root = this.hostRootRef()?.nativeElement;
    if (!root || this.portaledPanelObserver) {
      return;
    }

    const adopt = () => {
      for (const child of Array.from(document.body.children)) {
        if (child.matches(PORTALED_PANEL_SELECTOR)) {
          root.appendChild(child);
        }
      }
    };

    adopt();
    // Panels are portaled on open, which is long after fullscreen started.
    const observer = new MutationObserver(adopt);
    observer.observe(document.body, { childList: true });
    this.portaledPanelObserver = observer;
  }

  /** Field calendars and menus stay painted on the page after the fullscreen subtree is gone. */
  private dismissFullscreenChrome(): void {
    for (const picker of this.datetimePickers()) {
      picker.dismiss();
    }
    for (const select of this.chromeSelects()) {
      select.dismiss();
    }
  }

  private releasePortaledPanels(): void {
    this.portaledPanelObserver?.disconnect();
    this.portaledPanelObserver = null;

    const root = this.hostRootRef()?.nativeElement;
    const parents: ParentNode[] = [document.body];
    if (root) {
      parents.unshift(root);
    }
    const stranded: HTMLElement[] = [];
    for (const parent of parents) {
      for (const child of Array.from(parent.children)) {
        if (child instanceof HTMLElement && child.matches(PORTALED_PANEL_SELECTOR)) {
          stranded.push(child);
        }
      }
    }
    const drop = (): void => {
      for (const panel of stranded) {
        panel.hidden = true;
        panel.style.setProperty('display', 'none', 'important');
        panel.remove();
      }
    };
    drop();
    // Destroy and the picker's own position pass put the same node back.
    queueMicrotask(drop);
    setTimeout(drop);
    requestAnimationFrame(() => {
      drop();
      setTimeout(drop);
    });
  }

  /**
   * Keep Caliber's native 24–72h scale/slider. Seed AI motion filters, fit the
   * lookback into the viewport, and park the playhead on the alarm/anchor.
   *
   * `preserveView` keeps the user's zoom, scroll, and playhead untouched — a
   * stream restart after a scrub must not refit the scale under the cursor.
   */
  private applyHistoricAlarmSegmentsToCameraList(): void {
    const historic = this.historicAlarmSegments;
    const cameraId = this.historicAlarmCameraId;
    if (!historic || cameraId == null) {
      return;
    }
    this.cameraList = this.cameraList.map((camera) => {
      if (camera.id !== cameraId) {
        return camera;
      }
      return {
        ...camera,
        segments: {
          ...camera.segments,
          ...historic,
        } as unknown as PlaybackCamera['segments'],
      };
    });
  }

  /** Drops the site/camera cache so the next load matches the applied window. */
  private reloadHistoricTimelineAlarms(): void {
    this.historicAlarmsLoadKey = null;
    this.loadHistoricTimelineAlarms();
  }

  private loadHistoricTimelineAlarms(): void {
    if (this.mode() !== 'playback' || this.fullscreenMode() !== 'library') {
      return;
    }
    const siteId = this.siteId();
    const camera = this.playbackService.resolveCameraById(this.cameras(), this.cameraId());
    if (!siteId || !camera) {
      return;
    }
    const loadKey = `${siteId}:${camera.numericId}`;
    if (this.historicAlarmsLoadKey === loadKey) {
      if (this.historicAlarmSegments) {
        this.paintHistoricAlarmSegments();
      }
      return;
    }
    this.historicAlarmsLoadKey = loadKey;
    void firstValueFrom(
      this.playbackService.loadHistoricPlaybackAlarms(siteId, camera.numericId, [this.eventId()]),
    )
      .then((result) => {
        if (this.historicAlarmsLoadKey !== loadKey) {
          return;
        }
        this.historicAlarmRows = result.alarms;
        this.historicAlarmSegments = result.segments;
        this.historicAlarmCameraId = camera.numericId;
        this.paintHistoricAlarmSegments();
      })
      .catch(() => undefined);
  }

  private paintHistoricAlarmSegments(): void {
    this.applyHistoricAlarmSegmentsToCameraList();
    this.updateSelectedCameras$.next(this.cameraList);
    const player = this.resolveLibraryPlayer();
    if (!player) {
      return;
    }
    this.withoutLibraryStreamRestart(() => player.rebuildTimeline?.());
    this.refreshLibraryEventIndex(player, false);
  }

  private configureLibraryTimeline(preserveView = this.consumeLibraryPreservedView()): void {
    const page = this.playbackPage();
    if (!page) {
      return;
    }

    if (!this.libraryWindowCenter()) {
      this.libraryWindowCenter.set(
        this.clampLibraryStreamTime(this.resolveAnchorDate() ?? new Date()),
      );
    }
    const range = this.resolveNativeLibraryTimelineRange();
    page.startDate = range.start;
    page.endDate = range.end;

    const player = this.resolveLibraryPlayer();
    if (!player) {
      return;
    }

    const host = this.stageWrapRef()?.nativeElement.querySelector('lib-player');
    const viewportWidth =
      host?.querySelector('.timelineViewport')?.clientWidth ||
      host?.querySelector('.timelineContainer')?.clientWidth ||
      host?.clientWidth ||
      this.stageWrapRef()?.nativeElement.clientWidth ||
      800;

    player.startDate = range.start;
    player.endDate = range.end;
    player.min = 24;
    player.max = 72;
    player.step = 12;
    player.sliderValue = Math.min(72, Math.max(24, player.sliderValue || 24));
    player.tickerTimeZone = resolvePlaybackTimeZone(this.playbackCameraTimeZone());
    player.selectedMotions = this.resolveSelectedMotionValues();
    const anchor = this.resolveLibraryPlayheadAnchor(range.end);
    const clampedAnchor = new Date(
      Math.min(Math.max(anchor.getTime(), range.start.getTime()), range.end.getTime()),
    );

    if (!preserveView) {
      // Fit the slider lookback in view; wheel zoom still adjusts ppm afterward.
      const lookbackMinutes = player.sliderValue * 60;
      player.pixelsPerMinute = Math.max(1, Math.min(100, viewportWidth / lookbackMinutes));
      player.currentTickerTime = clampedAnchor;
      this.withoutLibraryStreamRestart(() => player.rebuildTimeline?.());
      // Scroll so the ticker/playhead sits on the alarm within the 24h world.
      const viewport =
        player.timelineScroller?.nativeElement ??
        (host?.querySelector('.timelineViewport') as HTMLElement | null);
      if (viewport && typeof player.engine?.getPixel === 'function') {
        const tickerX = this.libraryCursorOffset(player, viewport);
        viewport.scrollLeft = Math.max(0, player.engine.getPixel(clampedAnchor) - tickerX);
      }
    }

    const pinned = player.pinnedPlayhead;
    const playhead =
      pinned instanceof Date && !Number.isNaN(pinned.getTime())
        ? pinned
        : (player.currentTickerTime ?? clampedAnchor);
    if (playhead !== player.currentTickerTime) {
      player.currentTickerTime = playhead;
    }
    this.libraryCenterDate.set(playhead);
    // The chrome select owns the rate — a restart must not inherit Caliber's default.
    const speed = Number(this.librarySpeed());
    if (Number.isFinite(speed) && speed > 0) {
      player.selectedSpeed = speed;
      player.speeds = [...LIBRARY_PLAYBACK_SPEEDS];
    }
    this.applyLibraryPlaybackRate();
    this.wireLibraryTrimHelpers(player);
    this.wireLibraryTransportNav(player);
    this.wireLibraryPlayheadSeek(player);
    this.wireLibraryRangeControl(player);
    this.wireLibraryArchivePlayback(player);
    this.wireLibraryPlaybackPause(player);
    this.refreshLibraryEventIndex(player, !preserveView);
    this.bindLibraryViewportLoading();
    this.previewElapsedSeconds.set(
      Math.max(0, (playhead.getTime() - range.start.getTime()) / 1000),
    );
    queueMicrotask(() => this.syncLibraryChromePosition());
  }

  /**
   * Caliber Pause only stops its own stage video. The visible camera is the WHEP
   * overlay, so Pause and Play have to open and close that session.
   */
  private wireLibraryPlaybackPause(player: LibraryPlayerInstance): void {
    if (player.__eventqPauseWired || !player.onPause || !player.onPlay) {
      return;
    }
    const nativePause = player.onPause.bind(player);
    const nativePlay = player.onPlay.bind(player);
    player.onPause = () => {
      if (this.libraryPlaybackPaused) {
        return;
      }
      // A timeline drag pauses Caliber's own scroll loop. It must not close the stream.
      if (player.isDragging) {
        nativePause();
        this.stopLibraryPlayheadTicker();
        return;
      }
      nativePause();
      this.pauseLibraryTimeline();
    };
    player.onPlay = () => {
      if (player.isPlaying) {
        return;
      }
      const resumePaused = this.libraryPlaybackPaused;
      nativePlay();
      if (resumePaused) {
        this.resumeLibraryTimeline(player);
      }
    };
    player.__eventqPauseWired = true;
  }

  /**
   * Drive stage media from continuous `archive` tiles so play continues between
   * scarce AI bursts (motion/human/vehicle/gun) that paint on the timeline.
   */
  private wireLibraryArchivePlayback(player: LibraryPlayerInstance): void {
    if (player.__eventqArchiveSeekWired) {
      return;
    }
    const originalSync = player.syncStagePlayback?.bind(player);
    player.syncStagePlayback = (forceReload = false) => {
      const stage = this.playbackPage()?.stage as LibraryStageInstance | undefined;
      const time = player.currentTickerTime ?? new Date();
      const cameras = player.selectedCameraList?.length
        ? player.selectedCameraList
        : this.cameraList;
      const rate = player.selectedSpeed ?? 1;
      let matched = false;

      for (const cam of cameras) {
        const segment = resolveMediaSegmentAtTime(
          cam,
          [MOCK_ARCHIVE_SEGMENT_KEY, ...(player.selectedMotions ?? [])],
          time,
        );
        if (!segment || typeof stage?.seek !== 'function' || cam.id == null) {
          continue;
        }
        matched = true;
        try {
          stage.seek(cam.id, segment, time, !!player.isPlaying, rate, !!forceReload);
          const video =
            (typeof stage.getVideoElement === 'function'
              ? stage.getVideoElement(cam.id)
              : null) ?? stage.videoPlayers?.first?.nativeElement;
          if (video) {
            // Same short MP4 for the whole timescale — loop instead of ending.
            video.loop = true;
          }
        } catch {
          // jsdom / missing media APIs
        }
      }

      if (!matched) {
        originalSync?.(forceReload);
      }
    };
    player.__eventqArchiveSeekWired = true;
  }

  /** Park EventQ speed/datetime/AI controls over Caliber's selection strip. */
  private syncLibraryChromePosition(): void {
    const stage = this.stageWrapRef()?.nativeElement;
    if (!stage) {
      return;
    }
    const chrome = stage.querySelector(
      '.event-playback-host__library-chrome',
    ) as HTMLElement | null;
    const selection = stage.querySelector('.selectionContainer') as HTMLElement | null;
    if (!chrome || !selection) {
      return;
    }
    const stageRect = stage.getBoundingClientRect();
    const selectionRect = selection.getBoundingClientRect();
    chrome.style.top = `${Math.max(0, selectionRect.top - stageRect.top)}px`;
    chrome.style.left = `${Math.max(0, selectionRect.left - stageRect.left)}px`;
    chrome.style.width = `${selectionRect.width}px`;
    chrome.style.right = 'auto';
    chrome.style.height = `${Math.max(40, selectionRect.height)}px`;
  }

  private bindLibraryViewportLoading(): void {
    this.unbindLibraryViewportLoading();
    if (!this.useLibraryTimeline()) {
      return;
    }

    const host = this.stageWrapRef()?.nativeElement.querySelector('lib-player');
    const viewport =
      this.resolveLibraryPlayer()?.timelineScroller?.nativeElement ??
      (host?.querySelector('.timelineViewport') as HTMLElement | null);
    if (!viewport) {
      return;
    }

    const onViewChanged = (): void => this.scheduleLibraryVisibleSegmentFetch();
    viewport.addEventListener('scroll', onViewChanged, { passive: true });
    viewport.addEventListener('wheel', onViewChanged, { passive: true });
    this.libraryViewportUnbind = () => {
      viewport.removeEventListener('scroll', onViewChanged);
      viewport.removeEventListener('wheel', onViewChanged);
    };
  }

  private unbindLibraryViewportLoading(): void {
    if (this.libraryVisibleFetchTimer != null) {
      clearTimeout(this.libraryVisibleFetchTimer);
      this.libraryVisibleFetchTimer = null;
    }
    this.libraryViewportUnbind?.();
    this.libraryViewportUnbind = null;
  }

  private scheduleLibraryVisibleSegmentFetch(): void {
    if (!this.useLibraryTimeline() || this.mode() !== 'playback') {
      return;
    }
    // A WHEP timeline paints one continuous span — there is nothing to page in,
    // and a windowed load would replace the span the stream is playing from.
    if (this.shouldUseWebrtcPreview()) {
      return;
    }
    if (this.libraryVisibleFetchTimer != null) {
      clearTimeout(this.libraryVisibleFetchTimer);
    }
    this.libraryVisibleFetchTimer = setTimeout(() => {
      this.libraryVisibleFetchTimer = null;
      void this.fetchLibraryVisibleSegments({ preserveStream: true });
    }, 280);
  }

  /**
   * Refresh the WHEP archive span for the visible library window.
   * Recorded segment paging is gone — there is no dealer segments POST.
   */
  private async fetchLibraryVisibleSegments(_options?: {
    cameraIds?: number[];
    force?: boolean;
    preserveStream?: boolean;
  }): Promise<void> {
    if (!this.useLibraryTimeline() || this.mode() !== 'playback') {
      return;
    }

    if (this.shouldUseWebrtcPreview()) {
      this.seedLibraryWebrtcArchive(this.resolveNativeLibraryTimelineRange());
    }
  }

  /**
   * Caliber only shows trim clamps after Ctrl/Cmd+click on a segment while Clip
   * mode is active. Seed clamps around the playhead (or nearest AI segment) when
   * Clip is pressed so Cancel / Save + handles are immediately usable.
   * On Cancel, clear the selection — Caliber's onTrimClose only flips trimMode.
   */
  private wireLibraryTrimHelpers(player: LibraryPlayerInstance): void {
    if (player.__eventqTrimWired) {
      return;
    }

    const originalOnTrimClick = player.onTrimClick?.bind(player);
    if (typeof originalOnTrimClick !== 'function') {
      return;
    }

    const originalOnTrimClose = player.onTrimClose?.bind(player);

    player.onTrimClick = () => {
      originalOnTrimClick();
      this.seedLibraryTrimSelection(player);
    };

    player.onTrimClose = () => {
      originalOnTrimClose?.();
      player.trimMode = false;
      player.activeTrimSelection = null;
    };

    player.__eventqTrimWired = true;
  }

  private seedLibraryTrimSelection(player: LibraryPlayerInstance): void {
    if (typeof player.startTrimSelection !== 'function') {
      return;
    }

    const cameras = player.selectedCameraList ?? [];
    const camera = cameras[0];
    if (!camera) {
      return;
    }

    const ticker = player.currentTickerTime ?? new Date();
    const motions = (player.selectedMotionOptions?.length
      ? player.selectedMotionOptions
      : this.motionOptions) as Array<{ value: string }>;

    let best:
      | {
          segment: { start: Date | string; end: Date | string };
          eventType: string;
          distance: number;
        }
      | undefined;

    for (const motion of motions) {
      const segments = camera.segments?.[motion.value] ?? [];
      for (const segment of segments) {
        const start = new Date(segment.start).getTime();
        const end = new Date(segment.end).getTime();
        const inRange = ticker.getTime() >= start && ticker.getTime() <= end;
        const distance = inRange
          ? 0
          : Math.min(Math.abs(ticker.getTime() - start), Math.abs(ticker.getTime() - end));
        if (!best || distance < best.distance) {
          best = { segment, eventType: motion.value, distance };
        }
      }
    }

    if (best) {
      player.startTrimSelection(camera, best.segment, best.eventType);
      return;
    }

    // No AI segment nearby — still show clamps around the current playhead.
    const padMs = 5_000;
    const synthetic = {
      start: new Date(ticker.getTime() - padMs),
      end: new Date(ticker.getTime() + padMs),
    };
    player.startTrimSelection(camera, synthetic, motions[0]?.value ?? 'motion');
  }

  /** Native Caliber world: ±slider hours around the picked center, clipped at now. */
  private resolveNativeLibraryTimelineRange(): { start: Date; end: Date } {
    const player = this.resolveLibraryPlayer();
    const hours = this.resolveLibraryLookbackHours(player);
    const center = this.clampLibraryStreamTime(
      this.libraryWindowCenter() ?? this.resolveAnchorDate() ?? new Date(),
    );
    const start = new Date(center.getTime() - hours * 60 * 60_000);
    const end = this.clampLibraryStreamTime(
      new Date(center.getTime() + hours * 60 * 60_000),
    );
    return { start, end };
  }

  /**
   * Grow a window whose right edge is pinned to now. Historical windows whose
   * forward half is still in the past stay put.
   */
  extendLibraryWindowToNow(): void {
    if (!this.useLibraryTimeline()) {
      return;
    }
    const player = this.resolveLibraryPlayer();
    if (!player?.endDate) {
      return;
    }

    const range = this.resolveNativeLibraryTimelineRange();
    const previousEnd = player.endDate.getTime();
    const nextEnd = range.end.getTime();
    if (nextEnd <= previousEnd) {
      return;
    }

    const ticker = player.currentTickerTime;
    const parkedAtLiveEdge =
      ticker instanceof Date && Math.abs(previousEnd - ticker.getTime()) <= 1_500;
    const pixelsPerMinute = player.pixelsPerMinute;
    const viewport =
      player.timelineScroller?.nativeElement ??
      (this.stageWrapRef()?.nativeElement.querySelector('.timelineViewport') as HTMLElement | null);
    const scrollLeft = viewport?.scrollLeft;

    this.applyLibraryWindowRange(player, range);
    this.withoutLibraryStreamRestart(() => player.rebuildTimeline?.());

    if (typeof pixelsPerMinute === 'number') {
      player.pixelsPerMinute = pixelsPerMinute;
    }
    if (ticker instanceof Date) {
      player.currentTickerTime = parkedAtLiveEdge ? range.end : ticker;
    }
    if (viewport && typeof scrollLeft === 'number') {
      viewport.scrollLeft = scrollLeft;
    }
    if (parkedAtLiveEdge) {
      this.libraryCenterDate.set(range.end);
      if (player.pinnedPlayhead instanceof Date) {
        player.pinnedPlayhead = range.end;
      }
      return;
    }

    const pinned = player.pinnedPlayhead;
    if (pinned instanceof Date && !Number.isNaN(pinned.getTime())) {
      player.currentTickerTime = pinned;
      this.libraryCenterDate.set(pinned);
      this.centerLibraryViewportOn(player, pinned);
    }
  }

  private applyLibraryWindowRange(
    player: LibraryPlayerInstance,
    range: { start: Date; end: Date },
  ): void {
    player.startDate = range.start;
    player.endDate = range.end;
    const page = this.playbackPage();
    if (page) {
      page.startDate = range.start;
      page.endDate = range.end;
    }
    this.seedLibraryWebrtcArchive(range);
  }

  /**
   * Where the playhead parks when the timeline is rebuilt. A WHEP session plays
   * from its own start time, so re-initializing after a drag or calendar pick
   * must leave the ticker on that instant instead of snapping to the window end.
   */
  private resolveLibraryPlayheadAnchor(windowEnd: Date): Date {
    if (this.shouldUseWebrtcPreview()) {
      const startMs = untracked(() => this.resolveWebrtcStartMs());
      if (typeof startMs === 'number') {
        return new Date(startMs);
      }
      // No start time means the tile is live — the playhead belongs at the edge.
      return windowEnd;
    }
    return this.resolveAnchorDate() ?? windowEnd;
  }

  private resolveLibraryLookbackHours(player = this.resolveLibraryPlayer()): number {
    const requested = player?.sliderValue ?? 24;
    return Math.min(72, Math.max(24, Number.isFinite(requested) ? requested : 24));
  }

  private clampLibraryStreamTime(
    time: Date,
    player?: LibraryPlayerInstance | null,
    clampToTimeline = false,
  ): Date {
    const requested = Number.isFinite(time.getTime()) ? time.getTime() : Date.now();
    const latest = Math.min(requested, Date.now());
    if (!clampToTimeline || !player) {
      return new Date(Math.max(0, latest));
    }
    return new Date(
      Math.min(
        Math.max(latest, player.startDate?.getTime?.() ?? 0),
        player.endDate?.getTime?.() ?? Date.now(),
      ),
    );
  }

  private withLibraryWebrtcArchive(
    cameras: PlaybackCamera[],
    range: { start: Date; end: Date },
  ): PlaybackCamera[] {
    return cameras.map((camera) => {
      if (!camera.selected) {
        return camera;
      }
      const stream = this.playbackService.pickSegmentForCamera(
        camera,
        this.visibleDetectionTypes(),
      );
      if (!stream?.url) {
        return camera;
      }
      return {
        ...camera,
        segments: {
          ...camera.segments,
          [MOCK_ARCHIVE_SEGMENT_KEY]: [
            {
              ...stream,
              start: range.start,
              end: range.end,
            },
          ],
        },
      };
    });
  }

  private seedLibraryWebrtcArchive(range: { start: Date; end: Date }): void {
    if (!this.shouldUseWebrtcPreview()) {
      return;
    }
    this.cameraList = this.withLibraryWebrtcArchive(this.cameraList, range);
    this.updateSelectedCameras$.next(this.cameraList);
  }

  private resolveSelectedMotionValues(): string[] {
    // Library chrome AI Events select is the source of truth while the timeline is active.
    if (this.useLibraryTimeline()) {
      const fromSelect = this.libraryAiEventValues();
      const allowed = new Set(fromSelect.map(String));
      const matched = this.motionOptions
        .filter((option) => allowed.has(option.value))
        .map((option) => option.value);
      if (matched.length || this.strictVisibleDetectionTypes()) {
        return matched;
      }
      return this.motionOptions.map((option) => option.value);
    }

    const visible = this.visibleDetectionTypes();
    if (!visible.length) {
      // Event View: empty means “no alarm filter yet” → show all.
      // Strict (Showcase): empty means every AI type is off → show none.
      return this.strictVisibleDetectionTypes()
        ? []
        : this.motionOptions.map((option) => option.value);
    }

    const allowed = new Set(visible.map(String));
    const matched = this.motionOptions
      .filter((option) => allowed.has(option.value))
      .map((option) => option.value);
    if (matched.length) {
      return matched;
    }
    return this.strictVisibleDetectionTypes()
      ? []
      : this.motionOptions.map((option) => option.value);
  }

  private rematerializeHistoricAlarmSegments(): void {
    if (!this.historicAlarmRows || this.historicAlarmCameraId == null) {
      return;
    }
    const selected = this.resolveSelectedMotionValues().filter((value): value is EventAiTagVariant =>
      value === 'motion' || value === 'human' || value === 'vehicle' || value === 'gun',
    );
    this.historicAlarmSegments = mapHistoricAlarmsToTimelineSegments(
      this.historicAlarmRows,
      this.historicAlarmCameraId,
      selected,
    );
    this.applyHistoricAlarmSegmentsToCameraList();
    this.updateSelectedCameras$.next(this.cameraList);
  }

  /** Apply AI motion filter to Caliber without restarting playback. */
  private applyLibraryMotionFilter(): void {
    if (!this.useLibraryTimeline()) {
      return;
    }
    const player = this.resolveLibraryPlayer();
    if (!player) {
      return;
    }
    player.selectedMotions = this.resolveSelectedMotionValues();
    // While playing, only refresh the event index — rebuildTimeline re-syncs scroll/ticker
    // and can race stage load/play with another AbortError.
    if (player.isPlaying) {
      this.refreshLibraryEventIndex(player);
      return;
    }
    this.withoutLibraryStreamRestart(() => player.rebuildTimeline?.());
    this.refreshLibraryEventIndex(player);
  }

  private wireLibraryTransportNav(player: LibraryPlayerInstance): void {
    if (player.__eventqNavWired) {
      return;
    }
    player.__eventqNavWired = true;
    player.nextFrame = () => this.navigateLibraryTimeline('frame', 'next');
    player.previousFrame = () => this.navigateLibraryTimeline('frame', 'previous');
    player.nextEvent = () => this.navigateLibraryTimeline('event', 'next');
    player.previousEvent = () => this.navigateLibraryTimeline('event', 'previous');
    player.skipForward = (seconds = 10) => this.skipLibraryTimeline(seconds);
    player.skipBackward = (seconds = 10) => this.skipLibraryTimeline(-seconds);
  }

  /** Route Caliber playhead movement through WHEP session re-initialization. */
  private wireLibraryPlayheadSeek(player: LibraryPlayerInstance): void {
    if (player.__eventqPlayheadSeekWired || !player.seekToTime) {
      return;
    }
    const nativeSeek = player.seekToTime.bind(player);
    player.seekToTime = (time: Date, syncFromScroll?: boolean) => {
      const target = this.clampLibraryStreamTime(time, player, true);
      nativeSeek(target, syncFromScroll);
      this.libraryCenterDate.set(target);
      if (!this.suppressLibraryStreamReinitialize && !this.libraryPlaybackPaused) {
        this.reinitializeWebrtcAt(target);
      }
    };

    // Dragging the timeline never calls seekToTime — Caliber moves its own
    // ticker on scroll and only settles on release.
    const nativeDragEnd = player.onDragEnd?.bind(player);
    if (nativeDragEnd) {
      player.onDragEnd = () => {
        const wasDragging = player.isDragging === true;
        const resumePlayback = wasDragging && !this.libraryPlaybackPaused;
        nativeDragEnd();
        if (!wasDragging || this.suppressLibraryStreamReinitialize || this.libraryPlaybackPaused) {
          return;
        }
        const ticker = player.currentTickerTime;
        if (ticker) {
          const target = this.clampLibraryStreamTime(ticker, player, true);
          player.currentTickerTime = target;
          player.pinnedPlayhead = null;
          this.libraryCenterDate.set(target);
          this.reinitializeWebrtcAt(target);
        }
        if (resumePlayback) {
          player.isPlaying = true;
        }
      };
    }

    player.__eventqPlayheadSeekWired = true;
  }

  /**
   * The 24–72h slider re-spans the world around the frozen center. Caliber only
   * rebuilds off its own bound inputs, so widen and rebuild here — keeping the
   * zoom and playhead so the extra hours become scrollable instead of refitted.
   */
  private wireLibraryRangeControl(player: LibraryPlayerInstance): void {
    if (player.__eventqRangeWired) {
      return;
    }
    const nativeRangeChange = player.onRangeChange?.bind(player);
    player.onRangeChange = (hours: number) => {
      player.sliderValue = hours;
      this.withoutLibraryStreamRestart(() => nativeRangeChange?.(hours));

      const ticker = player.currentTickerTime;
      const pixelsPerMinute = player.pixelsPerMinute;
      const range = this.resolveNativeLibraryTimelineRange();
      this.applyLibraryWindowRange(player, range);
      if (typeof pixelsPerMinute === 'number') {
        player.pixelsPerMinute = pixelsPerMinute;
      }
      this.withoutLibraryStreamRestart(() => player.rebuildTimeline?.());

      if (ticker instanceof Date) {
        const parked = new Date(
          Math.min(Math.max(ticker.getTime(), range.start.getTime()), range.end.getTime()),
        );
        player.currentTickerTime = parked;
        this.centerLibraryViewportOn(player, parked);
      }
      this.refreshLibraryEventIndex(player, false);
    };
    player.__eventqRangeWired = true;
  }

  /**
   * Keep the selected instant under the playhead. Rebuilding the timeline
   * derives the clock from the old scroll position on the next frame.
   */
  private pinLibraryPlayhead(player: LibraryPlayerInstance, target: Date): void {
    player.currentTickerTime = target;
    this.centerLibraryViewportOn(player, target);
    if (typeof player.engine?.getPixel !== 'function') {
      return;
    }
    requestAnimationFrame(() => {
      if (this.libraryCenterDate()?.getTime() !== target.getTime()) {
        return;
      }
      player.currentTickerTime = target;
      this.centerLibraryViewportOn(player, target);
    });
  }

  /** Scroll the widened world so the playhead stays under the cursor. */
  private centerLibraryViewportOn(player: LibraryPlayerInstance, time: Date): void {
    const viewport =
      player.timelineScroller?.nativeElement ??
      (this.stageWrapRef()?.nativeElement.querySelector('.timelineViewport') as HTMLElement | null);
    if (!viewport || typeof player.engine?.getPixel !== 'function') {
      return;
    }
    viewport.scrollLeft = Math.max(0, player.engine.getPixel(time) - this.libraryCursorOffset(player, viewport));
  }

  /** White cursor inside the scroll area. Falls back to the scroll-area center. */
  private libraryCursorOffset(player: LibraryPlayerInstance, viewport: HTMLElement): number {
    const offset = player.cursorOffsetInViewport?.();
    return typeof offset === 'number' && Number.isFinite(offset) ? offset : viewport.clientWidth / 2;
  }

  private withoutLibraryStreamRestart(action: () => void): void {
    const wasSuppressed = this.suppressLibraryStreamReinitialize;
    this.suppressLibraryStreamReinitialize = true;
    try {
      action();
    } finally {
      this.suppressLibraryStreamReinitialize = wasSuppressed;
    }
  }

  private refreshLibraryEventIndex(
    player: LibraryPlayerInstance | null = this.resolveLibraryPlayer(),
    allowTimelineRebuild = true,
  ): void {
    if (!player) {
      return;
    }
    // Prefer Caliber's indexer so nextFrame/nextEvent stay on the same event list.
    // Never fall back to rebuildTimeline while playing — that can interrupt play().
    if (typeof player.rebuildEventIndex === 'function') {
      player.rebuildEventIndex();
      return;
    }
    // A rebuild re-derives the playhead from scroll, so it stays out of scrub restarts.
    if (!player.isPlaying && allowTimelineRebuild) {
      this.withoutLibraryStreamRestart(() => player.rebuildTimeline?.());
    }
  }

  private skipLibraryTimeline(deltaSeconds: number): void {
    const player = this.resolveLibraryPlayer();
    if (!player) {
      return;
    }
    const base = player.currentTickerTime ?? new Date();
    this.seekLibraryTimeline(new Date(base.getTime() + deltaSeconds * 1000));
  }

  private navigateLibraryTimeline(
    scope: LibraryTimelineNavScope,
    direction: LibraryTimelineNavDirection,
  ): void {
    const player = this.resolveLibraryPlayer();
    if (!player?.seekToTime) {
      return;
    }

    const selectedTypes = this.resolveSelectedMotionValues();
    const cameras = player.selectedCameraList?.length
      ? player.selectedCameraList
      : this.cameraList;
    const events = collectLibraryTimelineNavEvents(cameras, selectedTypes);
    const target = resolveLibraryTimelineNavTarget(
      events,
      player.currentTickerTime ?? new Date(),
      scope,
      direction,
      player.activeEventType,
    );
    if (!target) {
      return;
    }

    player.activeEventType = target.type;
    this.seekLibraryTimeline(new Date(target.start));
  }

  /**
   * Seek timeline + stage media. Caliber's stage.seek no-ops currentTime while
   * playing on the same URL — apply a soft seek (or force-reload) so Frame /
   * Event / ±10s work during playback.
   */
  private seekLibraryTimeline(time: Date): void {
    const player = this.resolveLibraryPlayer();
    if (!player?.seekToTime) {
      return;
    }

    const target = this.clampLibraryStreamTime(time, player, true);
    player.forceSeekNextUpdate = true;
    player.seekToTime(target, false);
    this.libraryCenterDate.set(target);
    if (!player.__eventqPlayheadSeekWired && !this.libraryPlaybackPaused) {
      this.reinitializeWebrtcAt(target);
    }

    const origin = player.startDate ?? this.resolveNativeLibraryTimelineRange().start;
    this.previewElapsedSeconds.set(
      Math.max(0, (target.getTime() - origin.getTime()) / 1000),
    );

    if (!player.isPlaying || this.shouldUseWebrtcPreview()) {
      return;
    }

    this.applyLibrarySeekWhilePlaying(target, player);
  }

  private resolveLibraryVisibleDetectionTypes(camera: EventCameraItem): EventAiTagVariant[] {
    const allowed = new Set<EventAiTagVariant>(['motion', 'human', 'vehicle', 'gun']);
    const fromSelect = this.libraryAiEventValues().filter((value): value is EventAiTagVariant =>
      allowed.has(value as EventAiTagVariant),
    );
    if (fromSelect.length) {
      return fromSelect;
    }
    const visible = this.visibleDetectionTypes().filter((value) => allowed.has(value));
    if (visible.length) {
      return visible;
    }
    const enabled = camera.enabledAiEvents.filter((value) => allowed.has(value));
    return enabled.length ? enabled : [...allowed];
  }

  private applyLibrarySeekWhilePlaying(time: Date, player: LibraryPlayerInstance): void {
    const stage = this.playbackPage()?.stage as LibraryStageInstance | undefined;
    if (!stage) {
      return;
    }

    const motions = player.selectedMotions?.length
      ? player.selectedMotions
      : this.resolveSelectedMotionValues();
    const cameras = player.selectedCameraList?.length
      ? player.selectedCameraList
      : this.cameraList;
    const rate = player.selectedSpeed ?? 1;

    for (const cam of cameras) {
      const segment = resolveMediaSegmentAtTime(cam, motions, time);
      if (!segment) {
        continue;
      }

      const video =
        (typeof stage.getVideoElement === 'function'
          ? stage.getVideoElement(cam.id)
          : null) ?? stage.videoPlayers?.first?.nativeElement;
      if (!video) {
        continue;
      }

      const entry = stage.playerMap?.get(cam.id);
      if (trySoftSeekPlayingVideo(video, entry, segment, time)) {
        continue;
      }

      // Outside the loaded window — match Caliber skipForward (forceReload).
      try {
        stage.seek?.(cam.id, segment, time, true, rate, true);
      } catch {
        // jsdom / missing media APIs
      }
      void video.play()?.catch(() => undefined);
    }
  }

  /**
   * Caliber does not export Player; resolve via public getDebugNode(host).componentInstance.
   */
  private resolveLibraryPlayer(): LibraryPlayerInstance | null {
    const host = this.stageWrapRef()?.nativeElement.querySelector('lib-player');
    if (!host) {
      return null;
    }

    const instance = resolveComponentFromHostElement<LibraryPlayerInstance>(host);
    return instance && typeof instance.rebuildTimeline === 'function' ? instance : null;
  }

  private bindStageTimeUpdates(): void {
    this.unbindStageTimeUpdates();
    const stage = this.playbackPage()?.stage;
    if (!stage?.videoTimeUpdate) {
      return;
    }

    this.stageTimeSub = stage.videoTimeUpdate.subscribe((event) => {
      if (!this.useLibraryTimeline()) {
        return;
      }

      // Offset origin is the library 24h world start so boxes match timeline AI segments.
      const origin =
        this.resolveLibraryPlayer()?.startDate ?? this.resolveNativeLibraryTimelineRange().start;
      const elapsedSeconds = (event.currentTime.getTime() - origin.getTime()) / 1000;
      this.previewElapsedSeconds.set(Math.max(0, elapsedSeconds));

      const video = stage.videoPlayers?.first?.nativeElement;
      if (video) {
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          this.videoIntrinsicSize.set({
            width: video.videoWidth,
            height: video.videoHeight,
          });
        }
        this.previewPlaying.set(!video.paused);
      }

      // Prefetch as the playhead approaches the edge of the loaded window.
      const loaded = this.libraryLoadedWindow;
      if (loaded) {
        const t = event.currentTime.getTime();
        const margin = Math.max((loaded.end - loaded.start) * 0.15, 2 * 60_000);
        if (t >= loaded.end - margin || t <= loaded.start + margin) {
          this.scheduleLibraryVisibleSegmentFetch();
        }
      }
    });
  }

  private unbindStageTimeUpdates(): void {
    this.stageTimeSub?.unsubscribe();
    this.stageTimeSub = null;
  }

  /**
   * Fullscreen timeline: seek Caliber stage to the alarm/playback anchor once video exists.
   */
  private async primeStageVideo(
    cameraId: number,
    anchor: Date,
    cameras: PlaybackCamera[] = this.cameraList,
  ): Promise<void> {
    const camera = cameras.find((entry) => entry.id === cameraId && entry.selected);
    if (!camera) {
      return;
    }

    const segment = this.playbackService.pickSegmentForCamera(
      camera,
      this.visibleDetectionTypes(),
    );
    if (!segment) {
      return;
    }

    for (let attempt = 0; attempt < 60; attempt++) {
      const page = this.playbackPage();
      const stage = page?.stage;
      const player = this.resolveLibraryPlayer();
      const isSelected = page?.selectedCameras?.some((entry) => entry.id === cameraId) ?? false;
      const video = stage?.videoPlayers?.first?.nativeElement as HTMLVideoElement | undefined;
      const hasVideo = !!video;
      if (stage && isSelected && hasVideo) {
        try {
          const alreadyLoaded = Boolean(video.currentSrc || video.src);
          const rate = player?.selectedSpeed ?? 1;
          // Avoid forceReload when a source is already loaded — reloading mid-play()
          // surfaces AbortError: The play() request was interrupted by a new load request.
          const seekSegment = resolveMediaSegmentAtTime(
            camera,
            this.visibleDetectionTypes(),
            anchor,
          ) ?? segment;
          stage.seek(cameraId, seekSegment, anchor, true, rate, !alreadyLoaded);
          video.loop = true;
          // Swallow AbortError from overlapping play()/load() races (benign in Chromium).
          void video.play()?.catch(() => undefined);
        } catch {
          // jsdom / missing media APIs can reject Caliber's internal play()
        }
        this.bindStageTimeUpdates();
        return;
      }
      await new Promise<void>((resolve) => setTimeout(resolve, 16));
    }
  }

  /** Nested frame-group detections stamped onto their original clip frame bands. */
  private applyAlarmClipDetectionBoxes(): void {
    const original = this.alarmClipSourceFrameIndexes();
    const frameCount = original.length
      ? Math.max(...original) + 1
      : this.alarmClipPlayableFrameCount();
    this.detectionBoxes.set(
      stampAlarmClipDetectionBoxes(
        this.alarmDetections(),
        frameCount,
        this.selectedAlarmId(),
      ),
    );
  }

  private alarmClipPlayableFrameCount(): number {
    return this.alarmClipFrameUrls().length || this.alarmClipImageSources().length;
  }

  private async loadDetections(
    camera: EventCameraItem,
    explicitTypes: EventAiTagVariant[],
    window: PlaybackWindow | null,
  ): Promise<void> {
    if (this.usesAlarmImageClip()) {
      this.applyAlarmClipDetectionBoxes();
      return;
    }

    const types = explicitTypes.length > 0 ? explicitTypes : camera.enabledAiEvents;

    // Library timeline: every painted AI segment owns a matching bbox track.
    if (this.useLibraryTimeline()) {
      this.refreshLibraryDetectionBoxes(camera.numericId, types);
      return;
    }

    const boxes = await this.playbackService.loadDetections(
      camera.numericId,
      camera.enabledAiEvents,
      types,
      window ? { start: window.start, durationSeconds: window.durationSeconds } : undefined,
      this.selectedAlarmId(),
    );
    this.detectionBoxes.set(boxes);
  }

  /** Derive detection overlays from the scarce AI segments currently on the camera. */
  private refreshLibraryDetectionBoxes(
    numericId: number,
    visibleTypes: EventAiTagVariant[],
  ): void {
    const playbackCamera =
      this.cameraList.find((entry) => entry.id === numericId) ??
      this.resolveLibraryPlayer()?.selectedCameraList?.find((entry) => entry.id === numericId);
    if (!playbackCamera?.segments) {
      this.detectionBoxes.set([]);
      return;
    }

    const origin =
      this.resolveLibraryPlayer()?.startDate ?? this.resolveNativeLibraryTimelineRange().start;
    const boxes = buildDetectionBoxesFromAiSegments(
      numericId,
      playbackCamera.segments,
      origin,
    );
    this.detectionBoxes.set(filterBoxesByEventTypes(boxes, visibleTypes));
  }

  private openAiFeedbackState(detectionId: string, variant: EventAiTagVariant): void {
    if (this.aiFeedbackScope() === 'view-alarm') {
      this.browserTabs.updateActivePageState({
        viewAlarmAiFeedbackOpen: true,
        viewAlarmAiFeedbackDetectionId: detectionId,
        viewAlarmAiFeedbackVariant: variant,
      });
      return;
    }

    this.browserTabs.updateActivePageState({
      aiFeedbackOpen: true,
      aiFeedbackCameraId: this.cameraId(),
      aiFeedbackDetectionId: detectionId,
      aiFeedbackVariant: variant,
    });
  }

  private clearAiFeedbackState(): void {
    if (this.aiFeedbackScope() === 'view-alarm') {
      this.browserTabs.updateActivePageState({
        viewAlarmAiFeedbackOpen: false,
        viewAlarmAiFeedbackDetectionId: null,
        viewAlarmAiFeedbackVariant: null,
      });
      return;
    }

    this.browserTabs.updateActivePageState({
      aiFeedbackOpen: false,
      aiFeedbackCameraId: null,
      aiFeedbackDetectionId: null,
      aiFeedbackVariant: null,
    });
  }
}
